import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Wand2, Copy, Settings, ArrowRight, MessageSquare, Send, GitCompare, X, User, Bot, Square, Check, History, ChevronLeft, ChevronRight, Clock, GitBranch, Play, Compass, BarChart3, Brain } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useAI } from '@/hooks/useAI';
import { showSuccessToast, showErrorToast } from '@/utils/error-handler';
import { generateEditReasoningPrompt, generateChangeProposalPrompt, generateCompleteCodePrompt, generateContinuationPrompt, getDSLSyntaxReference, isScriptLikelyComplete, detectUserFrustration, getWarmResponseInstructions, type ScriptEditingContext, type EditTarget, detectUserIntent, detectEditTarget, buildIntentClassificationPrompt, parseIntentClassificationResponse } from '@/services/ai/ai-prompts';
import { compileAndFix } from '@/services/ai/compile-and-fix-agent';
import { runAndAnalyze } from '@/services/ai/run-and-analyze-agent';
import { buildNestedPrompts, createNestedPrompt } from '@/services/ai/nested-prompts';
import { getAIWebSocketService } from '@/services/ai/ai-websocket';
import type { AIMessage } from '@/services/ai/ai-service';
import {
  type ChatMessage as SharedChatMessage,
  loadChatFromSession,
  saveChatToSession,
  parseChatFromJson as parseChatFromJsonShared,
} from '@/services/ai/chat-storage';
import type { SignalRiskAnalysis } from '@/components/signals/SignalRiskWidget';
import { diffLines } from 'diff';
import MarkdownRenderer from '@/components/ui/markdown';
import apiClient from '@/services/api/client';
import MonacoEditor from '@/components/editor/MonacoEditor';

interface AIScriptEditorProps {
  currentScript: string;
  onScriptUpdated: (updatedScript: string) => void;
  onConfigureClick?: () => void;
  existingSignals?: string[];
  datasetInfo?: {
    symbol?: string;
    timeframe?: string;
    columns?: string[];
  };
  compilationInfo?: {
    hasErrors?: boolean;
    errors?: Array<{ line?: number; column?: number; message: string; type?: string }>;
    warnings?: Array<{ line?: number; column?: number; message: string }>;
  };
  /** When Save was used with sidebar closed, parent passes the version to add; we add it and clear via callback */
  pendingSavedVersion?: { code: string; description: string } | null;
  onConsumePendingSavedVersion?: () => void;
  /** Optional script id for persisting chat to sessionStorage (per-script chat in session) */
  scriptId?: string | null;
  /** Chat JSON passed after creating a script (navigate state); used to seed chat so it isn't lost by timing */
  migratedChatFromState?: string | null;
  /** Unified chat: when set, use this state instead of internal (shared with AI Script Generation). Parent must persist. */
  unifiedChatMessages?: SharedChatMessage[];
  setUnifiedChatMessages?: React.Dispatch<React.SetStateAction<SharedChatMessage[]>>;
  /** When set, enables "Run and analyze" button: run execute job and append AI analysis to chat */
  runAndAnalyzeParams?: {
    scriptId: string;
    datasetId: string;
    symbol?: string;
    dateStart?: string;
    dateEnd?: string;
    scriptCode: string;
    scriptDescription?: string;
    datasetInfo?: {
      symbol?: string;
      timeframe?: string;
      columns?: string[];
      timezone?: string;
      sessionStrategy?: 'by_date' | 'custom' | 'trading_sessions';
      sessions?: Array<{ name: string; start_time: string; end_time: string; description?: string }>;
      sessionIncludeDate?: boolean;
      intervalDuration?: number | string;
      intervalIdFormat?: 'timestamp' | 'datetime';
    };
  } | null;
}

export interface AIScriptEditorHandle {
  createVersion: (code: string, description?: string) => void;
  /** Add Risk & Signals analysis to chat as a widget and trigger AI analysis with full context. */
  analyzeRiskInChat: (analysis: SignalRiskAnalysis, context?: RiskAnalysisContext) => Promise<void>;
}

/** Use shared ChatMessage type from chat-storage (alias for clarity in this file). */
type ChatMessage = SharedChatMessage;

/** Omit distribution arrays from risk analysis to keep context small and focused for the AI. */
function omitDistributions<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(omitDistributions) as T;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === 'distribution') continue;
    (out as Record<string, unknown>)[key] = omitDistributions(value);
  }
  return out as T;
}

export type RiskAnalysisContext = { evaluationMode: string; simulationBars?: number };

/** Build full risk analysis context for the AI (distributions omitted to reduce size). */
function buildRiskContextForAI(analysis: SignalRiskAnalysis, context?: RiskAnalysisContext): string {
  const meta = analysis.metadata;
  const best = analysis.ranking?.bestSignal;
  const ranking = analysis.ranking?.signalRanking?.slice(0, 10) ?? [];
  const trimmed = omitDistributions(analysis);
  const evalLine = context
    ? context.evaluationMode === 'hold'
      ? `Evaluation mode: Hold (${context.simulationBars ?? meta.simulationBars ?? 'N'} bars).`
      : `Evaluation mode: ${context.evaluationMode === 'day_close' ? 'Day Close' : context.evaluationMode === 'session_close' ? 'Session Close' : context.evaluationMode}.`
    : `Simulation bars: ${meta.simulationBars ?? 'N/A'}.`;
  const lines: string[] = [
    'The user has shared Risk & Signals analysis results. Full data below (distribution arrays omitted).',
    '',
    `Summary: ${meta.signalsAnalyzed} signals analyzed, confidence: ${meta.confidence}. ${evalLine}`,
    best?.name ? `Best signal: ${best.name} – ${best.reason ?? ''} (confidence score ${best.confidence ?? 'N/A'}).` : '',
    ranking.length ? `Ranking: ${ranking.map((r) => `${r.signalName} (score ${typeof r.score === 'number' ? r.score.toFixed(1) : r.score})`).join('; ')}.` : '',
    '',
    'Full JSON (metrics, MAE/MFE stats, optimal config; no distribution arrays):',
    '```json',
    JSON.stringify(trimmed, null, 2),
    '```',
  ];
  return lines.filter(Boolean).join('\n');
}

const AIScriptEditor = forwardRef<AIScriptEditorHandle, AIScriptEditorProps>(function AIScriptEditor({
  currentScript,
  onScriptUpdated,
  onConfigureClick,
  existingSignals,
  datasetInfo,
  compilationInfo,
  pendingSavedVersion,
  onConsumePendingSavedVersion,
  scriptId,
  migratedChatFromState,
  unifiedChatMessages,
  setUnifiedChatMessages,
  runAndAnalyzeParams,
}, ref) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [editRequest, setEditRequest] = useState('');
  const [editedScript, setEditedScript] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProposingChanges, setIsProposingChanges] = useState(false);
  const [isReasoning, setIsReasoning] = useState(false);
  // Internal chat state when not using unified (parent-controlled) chat
  const [internalChatMessages, setInternalChatMessages] = useState<ChatMessage[]>(() => {
    if (migratedChatFromState && scriptId && scriptId !== 'new') {
      const parsed = parseChatFromJsonShared(migratedChatFromState);
      if (parsed && parsed.length > 0) return parsed;
    }
    const fromScript = loadChatFromSession(scriptId);
    if (fromScript && fromScript.length > 0) return fromScript;
    if (scriptId && scriptId !== 'new') {
      const fromNew = loadChatFromSession('new');
      if (fromNew && fromNew.length > 0) {
        saveChatToSession(scriptId, fromNew);
        saveChatToSession('new', []);
        return fromNew;
      }
    }
    return [];
  });
  const chatMessages = unifiedChatMessages ?? internalChatMessages;
  const setChatMessages = setUnifiedChatMessages ?? setInternalChatMessages;
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingCode, setStreamingCode] = useState('');
  const [isStreamingCode, setIsStreamingCode] = useState(false);
  const [incompleteCode, setIncompleteCode] = useState<{ code: string; needsOutput: boolean; isShorter: boolean } | null>(null);
  const [isContinuingCode, setIsContinuingCode] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [codeToUse, setCodeToUse] = useState<string>('');
  const [codeValidationErrors, setCodeValidationErrors] = useState<Array<{ line?: number; column?: number; message: string; type?: string }>>([]);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [isFixingCompilation, setIsFixingCompilation] = useState(false);
  
  // Artifact versioning system: id = GUID, alias = v1, v1.1, v2 (manual saves get vN.1, vN.2)
  interface ArtifactVersion {
    id: string;
    alias: string;
    code: string;
    timestamp: Date;
    description?: string;
    isCurrent: boolean;
  }
  function generateGuid(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}-${Math.random().toString(36).slice(2, 11)}`;
  }
  function getNextAlias(prev: ArtifactVersion[], currentId: string | null, description?: string): string {
    const existingAliases = new Set(prev.map((v) => v.alias));
    const isManualSave = description === 'Saved';
    if (isManualSave && currentId) {
      const current = prev.find((v) => v.id === currentId);
      if (current) {
        const parts = current.alias.replace(/^v/, '').split('.').map((s) => parseInt(s, 10));
        if (parts.length >= 1 && !parts.some(Number.isNaN)) {
          if (parts.length === 1) {
            let candidate = current.alias + '.1';
            while (existingAliases.has(candidate)) candidate = candidate + '.1';
            return candidate;
          }
          const sameDepth = [...parts];
          sameDepth[sameDepth.length - 1] += 1;
          let candidate = 'v' + sameDepth.join('.');
          if (!existingAliases.has(candidate)) return candidate;
          candidate = current.alias + '.1';
          while (existingAliases.has(candidate)) candidate = candidate + '.1';
          return candidate;
        }
      }
    }
    let maxMajor = 0;
    for (const v of prev) {
      const m = v.alias.match(/^v(\d+)/);
      if (m) maxMajor = Math.max(maxMajor, parseInt(m[1], 10));
    }
    return `v${maxMajor + 1}`;
  }
  const [artifactVersions, setArtifactVersions] = useState<ArtifactVersion[]>([]);
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const hasInitialV0Ref = useRef(false);
  const artifactVersionsRef = useRef<ArtifactVersion[]>([]);
  const currentVersionIdRef = useRef<string | null>(null);
  useEffect(() => {
    artifactVersionsRef.current = artifactVersions;
  }, [artifactVersions]);
  useEffect(() => {
    currentVersionIdRef.current = currentVersionId;
  }, [currentVersionId]);

  // Apply pending version from parent (e.g. Save was clicked while sidebar was collapsed)
  // Defer to next tick so v0 effect has run and refs are up to date
  useEffect(() => {
    if (!pendingSavedVersion?.code?.trim() || !onConsumePendingSavedVersion) return;
    const code = pendingSavedVersion.code;
    const desc = pendingSavedVersion.description;
    const done = onConsumePendingSavedVersion;
    const t = setTimeout(() => {
      const newId = generateGuid();
      setArtifactVersions((prev) => {
        const currentId = currentVersionIdRef.current;
        const alias = getNextAlias(prev, currentId, desc);
        const newVersion: ArtifactVersion = {
          id: newId,
          alias,
          code,
          timestamp: new Date(),
          description: desc || alias,
          isCurrent: true
        };
        const updatedVersions = prev.map((v) => ({ ...v, isCurrent: false }));
        return [...updatedVersions, newVersion];
      });
      setCurrentVersionId(newId);
      done();
    }, 0);
    return () => clearTimeout(t);
  }, [pendingSavedVersion?.code, pendingSavedVersion?.description, onConsumePendingSavedVersion]);

  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const chatIdRef = useRef<string>(`chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const wsCleanupRef = useRef<(() => void) | null>(null);
  const editAbortControllerRef = useRef<AbortController | null>(null);
  /** Only auto-continue once per incomplete generation; reset on new edit or when complete. */
  const autoContinueAttemptedRef = useRef<boolean>(false);
  /** Base script being edited (editor or last message); used by continuation diff. */
  const editBaseScriptRef = useRef<string | null>(null);
  const { callAI, callAIChat, error, isConfigured } = useAI({
    onError: (err) => {
      showErrorToast(err, 'Failed to communicate with AI');
      setIsLoading(false);
      setIsChatLoading(false);
    }
  });

  // Ref: apply migrated chat from state only once per scriptId (avoid overwriting on later effect runs)
  const appliedMigratedChatRef = useRef<string | null>(null);

  // When script changes: load persisted chat from sessionStorage for this script, clear streaming/UI state
  // Prefer migratedChatFromState when present (navigate after create) so chat is never lost by timing
  // Skip when using unified chat (parent owns state and restores).
  useEffect(() => {
    if (setUnifiedChatMessages) return;
    // Clean up WebSocket connection if exists
    if (wsCleanupRef.current) {
      wsCleanupRef.current();
      wsCleanupRef.current = null;
    }

    // Reset applied-migrated ref when switching to a different script
    if (appliedMigratedChatRef.current !== null && appliedMigratedChatRef.current !== scriptId) {
      appliedMigratedChatRef.current = null;
    }

    let restored: ChatMessage[] | null = null;
    const isRealId = scriptId && scriptId !== 'new';

    // Prefer chat passed via navigation state (same render as new scriptId; no sessionStorage timing issue)
    if (isRealId && migratedChatFromState && appliedMigratedChatRef.current !== scriptId) {
      const parsed = parseChatFromJsonShared(migratedChatFromState);
      if (parsed && parsed.length > 0) {
        restored = parsed;
        saveChatToSession(scriptId, parsed);
        appliedMigratedChatRef.current = scriptId;
      }
    }
    if (!restored) restored = loadChatFromSession(scriptId);
    if (isRealId && (!restored || restored.length === 0)) {
      const fromNew = loadChatFromSession('new');
      if (fromNew && fromNew.length > 0) {
        restored = fromNew;
        saveChatToSession(scriptId, fromNew);
        saveChatToSession('new', []); // clear so next new script starts fresh
      }
    }
    // When scriptId is 'new', don't overwrite with empty: in-memory chat may not be persisted yet
    // (this effect runs on currentScript change, often before the persist effect has run)
    setChatMessages((prev) => {
      if (scriptId === 'new' && (!restored || restored.length === 0)) return prev;
      return restored ?? [];
    });
    setChatInput('');
    setIsChatLoading(false);
    setIsStreaming(false);
    setIsStreamingCode(false);
    setIsContinuingCode(false);
    setStreamingContent('');
    setStreamingCode('');
    setIncompleteCode(null);
    setEditedScript('');
    setEditRequest('');
    setShowCodeModal(false);
    setCodeToUse('');
    setCodeValidationErrors([]);
    setIsValidatingCode(false);
    setShowDiff(false);
    setIsReasoning(false);
    setIsProposingChanges(false);

    // Clear validation timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }

    // Abort any ongoing edit operation
    if (editAbortControllerRef.current) {
      editAbortControllerRef.current.abort();
      editAbortControllerRef.current = null;
    }

    // Generate new chat ID for new session
    chatIdRef.current = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, [currentScript, scriptId, migratedChatFromState, setUnifiedChatMessages]);

  // Persist chat to sessionStorage whenever messages change (skip when parent owns state)
  useEffect(() => {
    if (setUnifiedChatMessages) return;
    saveChatToSession(scriptId, chatMessages);
  }, [scriptId, chatMessages, setUnifiedChatMessages]);

  // Auto-scroll chat to bottom (including when streaming)
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isChatLoading, streamingContent, isStreaming]);

  // Auto-resize chat textarea
  useEffect(() => {
    if (chatTextareaRef.current) {
      chatTextareaRef.current.style.height = 'auto';
      chatTextareaRef.current.style.height = `${chatTextareaRef.current.scrollHeight}px`;
    }
  }, [chatInput]);

  const handleConfigure = () => {
    if (onConfigureClick) {
      onConfigureClick();
    } else {
      navigate('/settings');
    }
  };

  /** Get code from the last assistant message (code block or DSL-like content). Used for "edit last message" intent. */
  const getLastAssistantCode = (messages: ChatMessage[]): string | null => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role !== 'assistant') continue;
      const content = messages[i].content;
      const match = content.match(/```[\w]*\n?([\s\S]*?)\n?```/);
      if (match) return match[1].trim();
      if (/\b(output\s+(table|stats|signals)|input\s+\w+\s*=|\bsignal\s+\w+)/m.test(content)) {
        const incomplete = content.match(/```[\w]*\n?([\s\S]*?)(?:\n?```|$)/);
        if (incomplete) return incomplete[1].trim();
      }
      return null;
    }
    return null;
  };

  const handleEdit = async (overrides?: { baseScript?: string; request?: string; skipAddUserMessage?: boolean }) => {
    const requestText = overrides?.request ?? editRequest.trim();
    const scriptToEdit = overrides?.baseScript ?? currentScript;
    const skipAddUserMessage = overrides?.skipAddUserMessage ?? false;

    if (!requestText) {
      showErrorToast('Please describe what you want to change');
      return;
    }

    if (!isConfigured) {
      showSuccessToast('Please configure your AI API key to edit scripts');
      handleConfigure();
      return;
    }

    if (!scriptToEdit.trim()) {
      showErrorToast('No script to edit. Please load a script first.');
      return;
    }

    // If user edited the script in the main editor manually, save that as a version before generating a new one
    const isEditingEditorContent = scriptToEdit === currentScript;
    if (isEditingEditorContent && currentScript.trim()) {
      const currentVersion = currentVersionId
        ? artifactVersions.find(v => v.id === currentVersionId)
        : null;
      const currentVersionCode = (currentVersion?.code ?? '').trim();
      const editorCode = currentScript.trim();
      if (currentVersionCode !== editorCode) {
        createArtifactVersion(currentScript, 'Manual edit');
      }
    }

    // Cancel any previous edit operation
    if (editAbortControllerRef.current) {
      editAbortControllerRef.current.abort();
    }

    // Create new AbortController for this operation
    const abortController = new AbortController();
    editAbortControllerRef.current = abortController;
    autoContinueAttemptedRef.current = false;
    editBaseScriptRef.current = scriptToEdit;

    setIsLoading(true);
    setIsReasoning(true);
    setIsProposingChanges(false);

    if (!skipAddUserMessage) {
      const userEditMessage: ChatMessage = {
        role: 'user',
        content: requestText,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, userEditMessage]);
    }

    try {
      const editContext: ScriptEditingContext = {
        currentScript: scriptToEdit,
        userRequest: requestText,
        existingSignals,
        datasetInfo,
        compilationInfo
      };

      // PHASE 0: Reasoning
      const reasoningPrompt = generateEditReasoningPrompt(editContext);
      const reasoningSystemPrompt = `You are an expert quantitative analyst and QuantQuack DSL developer. Analyze edit requests and provide clear reasoning about what needs to change and why.`;

      const reasoningResponse = await callAI(reasoningPrompt, reasoningSystemPrompt, {
        temperature: 0.7,
        maxTokens: 1000,
        signal: abortController.signal,
      });

      // Check if operation was aborted
      if (abortController.signal.aborted) {
        return;
      }

      if (!reasoningResponse) {
        showErrorToast('No response from AI for reasoning. Please try again.');
        setIsReasoning(false);
        setIsLoading(false);
        return;
      }

      const reasoningText = reasoningResponse.trim();
      setIsReasoning(false);
      setIsProposingChanges(true);

      // Add reasoning message to chat after user's edit request
      const reasoningMessage: ChatMessage = {
        role: 'assistant',
        content: `**Reasoning Analysis:**\n\n${reasoningText}`,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, reasoningMessage]);

      // PHASE 1: Propose changes (with reasoning)
      const proposalPrompt = generateChangeProposalPrompt(editContext, reasoningText);
      const proposalSystemPrompt = `You are an expert QuantQuack DSL developer. Analyze user requests and propose specific line-by-line changes to scripts.`;

      const proposalResponse = await callAI(proposalPrompt, proposalSystemPrompt, {
        temperature: 0.3,
        maxTokens: 1500,
        signal: abortController.signal,
      });

      // Check if operation was aborted
      if (abortController.signal.aborted) {
        return;
      }

      if (!proposalResponse) {
        showErrorToast('No response from AI for change proposal. Please try again.');
        setIsProposingChanges(false);
        setIsLoading(false);
        return;
      }

      const proposedChangesText = proposalResponse.trim();
      setIsProposingChanges(false);

      // Add proposed changes message to chat
      const proposedChangesMessage: ChatMessage = {
        role: 'assistant',
        content: `**Proposed Changes:**\n\n${proposedChangesText}`,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, proposedChangesMessage]);

      // PHASE 2: Generate complete code with streaming
      const codePrompt = generateCompleteCodePrompt(editContext, proposedChangesText, reasoningText);
      const codeSystemPrompt = `You are an expert QuantQuack DSL developer. Generate the COMPLETE script from first line to last (including "output table" or "output stats" or "output signals"). Do NOT truncate. Do NOT stop before the final output line. No explanations after the code.`;

      setIsLoading(true);
      setIsStreamingCode(true);
      setStreamingCode('');

      // Use WebSocket streaming for code generation
      const generateCodeWithStreaming = async (prompt: string, systemPrompt: string): Promise<{ code: string; streamingState: string; endedDueToTokens: boolean }> => {
        // Get AI config first
        const { getAIConfig } = await import('@/services/ai/ai-config');
        const config = getAIConfig();
        if (!config) {
          throw new Error('AI configuration not found');
        }

        return new Promise((resolve, reject) => {
          const wsService = getAIWebSocketService();
          const codeChatId = `code-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          let accumulatedCode = '';
          let codeCleanup: (() => void) | null = null;
          let lastStreamingState = ''; // Track the last streaming state

          wsService.connect(codeChatId, (message) => {
            if (abortController.signal.aborted) {
              if (codeCleanup) codeCleanup();
              reject(new Error('Operation aborted'));
              return;
            }

            if (message.type === 'chunk' && message.content) {
              accumulatedCode += message.content;
              // Clean up markdown code blocks in real-time
              let cleanedStreaming = accumulatedCode.trim();
              if (cleanedStreaming.startsWith('```')) {
                cleanedStreaming = cleanedStreaming.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
              }
              // Update streaming state in real-time
              lastStreamingState = cleanedStreaming;
              setStreamingCode(cleanedStreaming);
              
              // Auto-scroll
              setTimeout(() => {
                chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 50);
            } else if (message.type === 'done') {
              // DON'T clear streaming state yet - preserve it for incomplete code detection
              // setIsStreamingCode(false); // Keep it true until we determine if code is complete
              
              // Enhanced final cleanup - remove all markdown artifacts
              let finalCode = accumulatedCode.trim();
              
              // Detect if code is inside an incomplete markdown block BEFORE cleaning
              const rawCode = finalCode;
              const hasOpeningMarkdown = /```[\w]*\s*$/.test(rawCode) || /```[\w]*\n/.test(rawCode);
              const hasClosingMarkdown = /```\s*$/.test(rawCode);
              const isIncompleteMarkdownBlock = hasOpeningMarkdown && !hasClosingMarkdown;
              
              // Remove markdown code block markers
              finalCode = finalCode.replace(/^```[\w]*\n?/gm, '').replace(/\n?```$/gm, '');
              // Remove any text before code blocks
              const codeBlockMatch = finalCode.match(/```[\w]*\n?([\s\S]*?)\n?```/);
              if (codeBlockMatch) {
                finalCode = codeBlockMatch[1].trim();
              }
              // Remove any remaining markdown artifacts
              finalCode = finalCode.split(/```[\w]*/).pop() || finalCode;
              finalCode = finalCode.split(/```/).shift() || finalCode;
              finalCode = finalCode.trim();
              
              // Check if stream ended due to token limit
              const endedDueToTokens = message.finishReason === 'length';
              
              // Preserve the streaming state - use the most complete version
              // Prefer lastStreamingState (has real-time cleaning) over finalCode (has full cleanup)
              const preservedState = lastStreamingState || finalCode;
              
              if (codeCleanup) codeCleanup();
              
              // Resolve with code, preserved state, and flags
              resolve({ 
                code: finalCode, 
                streamingState: preservedState, // Preserve the streaming state
                endedDueToTokens: endedDueToTokens || isIncompleteMarkdownBlock 
              });
            } else if (message.type === 'error') {
              setIsStreamingCode(false);
              setStreamingCode(''); // Clear on error
              if (codeCleanup) codeCleanup();
              reject(new Error(message.error || 'Streaming error'));
            }
          }).then((cleanup) => {
            codeCleanup = cleanup;

            // Wait for WebSocket to be ready, then send request
            const sendRequest = () => {
              if (wsService.isConnected(codeChatId)) {
                const success = wsService.sendChatRequest(codeChatId, {
                  provider: config.provider,
                  apiKey: config.apiKey,
                  messages: [
                    { role: 'system', content: systemPrompt },
                    { role: 'user', content: prompt }
                  ],
                  model: config.model,
                  temperature: 0.3,
                  maxTokens: 8000
                });

                if (!success) {
                  setIsStreamingCode(false);
                  if (codeCleanup) codeCleanup();
                  reject(new Error('Failed to send streaming request'));
                }
              } else {
                setTimeout(sendRequest, 100);
              }
            };

            setTimeout(sendRequest, 200);
          }).catch((err) => {
            setIsStreamingCode(false);
            if (codeCleanup) codeCleanup();
            reject(err);
          });
        });
      };

        try {
          const result = await generateCodeWithStreaming(codePrompt, codeSystemPrompt);
          let cleanedCode = result.code;
          const preservedStreamingState = result.streamingState || result.code; // Use preserved streaming state
          const endedDueToTokens = result.endedDueToTokens || false;
          
          // Update streaming code with preserved state before analysis
          setStreamingCode(preservedStreamingState);

          // Check if operation was aborted
          if (abortController.signal.aborted) {
            return;
          }

          // Clean code more thoroughly - remove any remaining markdown artifacts
          // But first, check the RAW code before cleaning for truncated markdown
          const rawCodeBeforeCleaning = cleanedCode;
          // Check for opening markdown block (```dsl or ``` at start or middle)
          const hasOpeningMarkdown = /```[\w]*\s*$/.test(rawCodeBeforeCleaning) || 
                                     /```[\w]*\n/.test(rawCodeBeforeCleaning) ||
                                     rawCodeBeforeCleaning.includes('```');
          // Check for closing markdown block (``` at the end)
          const hasClosingMarkdown = /```\s*$/.test(rawCodeBeforeCleaning);
          // If we have opening but no closing, markdown is truncated
          const isIncompleteMarkdownBlock = hasOpeningMarkdown && !hasClosingMarkdown;
          
          console.log('Markdown truncation check:', {
            rawCodeLength: rawCodeBeforeCleaning.length,
            hasOpeningMarkdown,
            hasClosingMarkdown,
            isIncompleteMarkdownBlock,
            rawCodeEnd: rawCodeBeforeCleaning.slice(-50)
          });
          
          let finalCleanedCode = cleanedCode.trim();
          // Remove any leading/trailing markdown code block markers
          finalCleanedCode = finalCleanedCode.replace(/^```[\w]*\n?/gm, '').replace(/\n?```$/gm, '');
          // Remove any explanatory text before/after code blocks
          finalCleanedCode = finalCleanedCode.split(/```[\w]*/).pop() || finalCleanedCode;
          finalCleanedCode = finalCleanedCode.split(/```/).shift() || finalCleanedCode;
          finalCleanedCode = finalCleanedCode.trim();
          
          // If we detected incomplete markdown block, code is definitely truncated
          const hasTruncatedMarkdown = isIncompleteMarkdownBlock;
          
          // Use diff to detect incomplete code by comparing with original
          const originalLines = scriptToEdit.split('\n');
          const generatedLines = finalCleanedCode.split('\n');
          const diff = diffLines(scriptToEdit, finalCleanedCode);
          
          // Analyze diff to detect truncation patterns
          let removedLinesAtEnd = 0;
          let addedLinesAtEnd = 0;
          let consecutiveRemovedAtEnd = 0;
          let hasRemovedOutput = false;
          let lastOperationWasRemoved = false;
          
          // Check from the end backwards
          for (let i = diff.length - 1; i >= 0; i--) {
            const part = diff[i];
            if (part.removed) {
              consecutiveRemovedAtEnd += part.count || 0;
              removedLinesAtEnd += part.count || 0;
              lastOperationWasRemoved = true;
              
              // Check if removed lines contain output statement
              if (part.value && /\boutput\s+(table|stats|signals|stats\s+by)/m.test(part.value)) {
                hasRemovedOutput = true;
              }
            } else if (part.added) {
              if (lastOperationWasRemoved) {
                // If we hit added lines after removed, stop counting end removals
                break;
              }
              addedLinesAtEnd += part.count || 0;
            } else {
              // Unchanged lines - if we hit these after removed lines, stop counting
              if (lastOperationWasRemoved) {
                break;
              }
            }
          }
          
          // Additional checks
          const originalScriptHasOutput = /\boutput\s+(table|stats|signals|stats\s+by)/m.test(scriptToEdit);
          const generatedHasOutput = /\boutput\s+(table|stats|signals|stats\s+by)/m.test(finalCleanedCode);
          const isMissingOutput = !generatedHasOutput && originalScriptHasOutput;
          
          const originalLineCount = originalLines.filter((line: string) => line.trim().length > 0).length;
          const generatedLineCount = generatedLines.filter((line: string) => line.trim().length > 0).length;
          const lineCountRatio = generatedLineCount / originalLineCount;
          
          // Check if significant portion was removed at the end (indicates truncation)
          const significantRemovalAtEnd = consecutiveRemovedAtEnd > 5 || 
                                          (consecutiveRemovedAtEnd > 0 && lineCountRatio < 0.8);
          
          // Check if code is significantly shorter overall
          const isSignificantlyShorter = lineCountRatio < 0.75;
          
          // Check last line of generated code for truncation signs
          const lastGeneratedLine = generatedLines[generatedLines.length - 1]?.trim() || '';
          const lastOriginalLine = originalLines[originalLines.length - 1]?.trim() || '';
          
          // If original ends with output but generated doesn't, likely incomplete
          const originalEndsWithOutput = /\boutput\s+(table|stats|signals|stats\s+by)/m.test(lastOriginalLine);
          const generatedEndsWithOutput = /\boutput\s+(table|stats|signals|stats\s+by)/m.test(lastGeneratedLine);
          const missingEndOutput = originalEndsWithOutput && !generatedEndsWithOutput;
          
          // Determine if code is incomplete - use more intelligent detection
          // Only mark as incomplete if there are STRONG indicators
          let isIncomplete = false;
          
          // STRONG indicators (definitely incomplete):
          // 1. Code is inside an incomplete markdown block (truncated markdown)
          if (hasTruncatedMarkdown) {
            isIncomplete = true;
            console.log('Code incomplete: Truncated markdown block detected');
          }
          // 2. Stream ended due to token limit
          else if (endedDueToTokens) {
            isIncomplete = true;
            console.log('Code incomplete: Stream ended due to token limit');
          }
          // 3. Missing output statement when original had it
          else if (isMissingOutput && originalScriptHasOutput) {
            isIncomplete = true;
            console.log('Code incomplete: Missing output statement');
          }
          // 4. Output was explicitly removed in diff
          else if (hasRemovedOutput) {
            isIncomplete = true;
            console.log('Code incomplete: Output statement was removed');
          }
          // 5. Very large truncation at end (>15 lines removed consecutively)
          else if (consecutiveRemovedAtEnd > 15) {
            isIncomplete = true;
            console.log(`Code incomplete: Large truncation at end (${consecutiveRemovedAtEnd} lines removed)`);
          }
          // 6. Extremely short (<50% of original) AND missing output
          else if (lineCountRatio < 0.5 && !generatedHasOutput && originalScriptHasOutput) {
            isIncomplete = true;
            console.log(`Code incomplete: Extremely short (${(lineCountRatio * 100).toFixed(0)}%) and missing output`);
          }
          // 7. Missing output at the end when original ended with output
          else if (missingEndOutput && consecutiveRemovedAtEnd > 5) {
            isIncomplete = true;
            console.log('Code incomplete: Missing output at end with significant removal');
          }
          
          // If stream ended normally (not due to tokens) and code has output,
          // it's likely complete even if shorter (could be optimized/refactored)
          // BUT: if markdown is truncated, it's definitely incomplete
          if (!hasTruncatedMarkdown && !endedDueToTokens && generatedHasOutput && !isIncomplete) {
            if (consecutiveRemovedAtEnd <= 30) {
              isIncomplete = false;
              console.log('Code complete: Stream ended normally, has output, no major truncation');
            }
          }
          // Script already ends with output (possibly followed by comments/decorations like # === or ======)
          if (isIncomplete && isScriptLikelyComplete(finalCleanedCode).complete) {
            isIncomplete = false;
            console.log('Code complete: Script ends with output (trailing comments/decorations OK)');
          }
          
          console.log('Code completeness check (using diff):', {
            originalLines: originalLineCount,
            generatedLines: generatedLineCount,
            lineCountRatio: lineCountRatio.toFixed(2),
            hasOutput: generatedHasOutput,
            originalHasOutput: originalScriptHasOutput,
            hasTruncatedMarkdown,
            endedDueToTokens,
            isMissingOutput,
            hasRemovedOutput,
            removedLinesAtEnd,
            consecutiveRemovedAtEnd,
            significantRemovalAtEnd,
            isSignificantlyShorter,
            missingEndOutput,
            isIncomplete,
            lastGeneratedLine: lastGeneratedLine.substring(0, 50),
            lastOriginalLine: lastOriginalLine.substring(0, 50)
          });
          
          if (isIncomplete) {
            // Code is incomplete - show Continue button instead of auto-continuing
            console.log(`Code appears incomplete (diff analysis) - showing Continue button`, {
              missingOutput: isMissingOutput,
              hasRemovedOutput,
              significantRemovalAtEnd,
              isSignificantlyShorter,
              missingEndOutput,
              removedLinesAtEnd: consecutiveRemovedAtEnd,
              hasTruncatedMarkdown,
              endedDueToTokens
            });
            
            // Use the preserved streaming state (most accurate representation of what was streamed)
            // Fallback to cleaned code if streaming state is not available
            const codeToPreserve = preservedStreamingState.trim() || finalCleanedCode;
            
            const incompleteCodeData = {
              code: codeToPreserve, // Use the preserved streaming state
              needsOutput: isMissingOutput || hasRemovedOutput || missingEndOutput,
              isShorter: isSignificantlyShorter || significantRemovalAtEnd
            };
            
            console.log('Setting incompleteCode with preserved streaming state:', {
              incompleteCodeData: { ...incompleteCodeData, codePreview: codeToPreserve.substring(0, 100) },
              preservedStreamingStateLength: preservedStreamingState.length,
              finalCleanedCodeLength: finalCleanedCode.length,
              streamingCodeLength: streamingCode.length
            });
            
            setIncompleteCode(incompleteCodeData);
            // Keep streamingCode visible with the preserved state
            setStreamingCode(codeToPreserve);
            setIsStreamingCode(false); // Stop streaming indicator but keep code visible
            setIsLoading(false);
            
            // Build reason message based on diff analysis and token limit
            const reasons: string[] = [];
            if (hasTruncatedMarkdown) {
              reasons.push('truncated markdown block');
            }
            if (endedDueToTokens) {
              reasons.push('stream ended due to token limit');
            }
            if (isMissingOutput || hasRemovedOutput || missingEndOutput) {
              reasons.push('missing output statement');
            }
            if (isSignificantlyShorter) {
              reasons.push(`too short (${generatedLineCount} vs ${originalLineCount} lines, ${(lineCountRatio * 100).toFixed(0)}%)`);
            }
            if (significantRemovalAtEnd && consecutiveRemovedAtEnd > 0) {
              reasons.push(`truncated at end (${consecutiveRemovedAtEnd} lines removed)`);
            }
            
            // Add incomplete code message to chat with Continue button
            const incompleteMessage: ChatMessage = {
              role: 'assistant',
              content: `**Generated Code (Incomplete):**\n\n\`\`\`dsl\n${finalCleanedCode}\n\`\`\`\n\n⚠️ Code generation appears incomplete: ${reasons.join(', ')}. Click "Continue" to generate the remaining code.`,
              timestamp: new Date()
            };
            setChatMessages(prev => [...prev, incompleteMessage]);
            
            setEditedScript(finalCleanedCode);
            console.log('Incomplete code state set, Continue button should appear');
            // Auto-continue once so the user often gets complete code without clicking
            if (!autoContinueAttemptedRef.current) {
              autoContinueAttemptedRef.current = true;
              setTimeout(() => {
                handleContinueCode();
              }, 800);
            }
            return; // Stop here; continuation will run or user can click Continue
          }

          // Code is complete - clear temporary state
          setIncompleteCode(null);
          setStreamingCode(''); // Clear temporary streaming state
          setIsStreamingCode(false);
          autoContinueAttemptedRef.current = false;

        // Use the preserved streaming state for the final code
        const finalCodeToUse = preservedStreamingState.trim() || finalCleanedCode;
        setEditedScript(finalCodeToUse);
        
        // Create artifact version for the generated code
        const newVersion = createArtifactVersion(finalCodeToUse, 'AI generated code');
        
        // Clear streaming state now that code is complete
        setStreamingCode('');
        setIsStreamingCode(false);
        
        // Add final code message to chat
        const codeMessage: ChatMessage = {
          role: 'assistant',
          content: `**Generated Code (${newVersion.alias}):**\n\n\`\`\`dsl\n${finalCodeToUse}\n\`\`\``,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, codeMessage]);
        
        showSuccessToast(`Script edited successfully! (saved as version ${newVersion.alias})`);
      } catch (codeErr) {
        if (!abortController.signal.aborted) {
          console.error('Error generating code:', codeErr);
          showErrorToast('Failed to generate code. Please try again.');
        }
        setStreamingCode('');
        setIsStreamingCode(false);
      }
    } catch (err) {
      // Only show error if not aborted
      if (!abortController.signal.aborted) {
        console.error('Error in handleEdit:', err);
        const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred while editing the script';
        showErrorToast(errorMessage, 'Failed to edit script');
      }
    } finally {
      // Only reset if this is still the current operation
      if (editAbortControllerRef.current === abortController) {
        setIsLoading(false);
        setIsReasoning(false);
        setIsProposingChanges(false);
        editAbortControllerRef.current = null;
      }
    }
  };

  // Seed v0 (original script) in history when we have a script and no versions yet
  useEffect(() => {
    const script = (currentScript || '').trim();
    if (!script || hasInitialV0Ref.current) return;
    if (artifactVersions.length > 0) return;
    hasInitialV0Ref.current = true;
    const v0: ArtifactVersion = {
      id: generateGuid(),
      alias: 'v0',
      code: script,
      timestamp: new Date(),
      description: 'Original script',
      isCurrent: true
    };
    setArtifactVersions([v0]);
    setCurrentVersionId(v0.id);
  }, [currentScript, artifactVersions.length]);

  // Create new artifact version (reads latest state from refs so it works when called from parent Save)
  const createArtifactVersion = (code: string, description?: string): ArtifactVersion => {
    const prev = artifactVersionsRef.current;
    const currentId = currentVersionIdRef.current;
    const alias = getNextAlias(prev, currentId, description);
    const newVersion: ArtifactVersion = {
      id: generateGuid(),
      alias,
      code,
      timestamp: new Date(),
      description: description || alias,
      isCurrent: true
    };
    const updatedVersions = prev.map((v) => ({ ...v, isCurrent: false }));
    setArtifactVersions([...updatedVersions, newVersion]);
    setCurrentVersionId(newVersion.id);
    return newVersion;
  };

  const analyzeRiskInChat = async (analysis: SignalRiskAnalysis, context?: RiskAnalysisContext): Promise<void> => {
    const riskMessage = {
      role: 'user' as const,
      content: t('signals.riskSignalsAnalysis'),
      timestamp: new Date(),
      type: 'risk_analysis' as const,
      riskAnalysis: analysis,
      riskAnalysisContext: context,
    } satisfies ChatMessage;
    setChatMessages((prev) => [...prev, riskMessage]);
    const allMessages: ChatMessage[] = [...chatMessages, riskMessage];
    const apiMessages: AIMessage[] = allMessages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => {
        const cm = m as ChatMessage;
        if (cm.type === 'risk_analysis' && cm.riskAnalysis) {
          return { role: 'user' as const, content: buildRiskContextForAI(cm.riskAnalysis as SignalRiskAnalysis, cm.riskAnalysisContext) };
        }
        return { role: m.role as 'user' | 'assistant', content: m.content };
      });
    apiMessages.push({
      role: 'user',
      content: 'Please analyze these Risk & Signals results and provide insights, recommendations, and which signals look most promising.',
    });
    setIsChatLoading(true);
    try {
      const response = await callAIChat(apiMessages, { temperature: 0.5, maxTokens: 2000 });
      if (response) {
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: response,
          timestamp: new Date(),
        };
        setChatMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'AI analysis failed');
    } finally {
      setIsChatLoading(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      createVersion(code: string, description?: string) {
        createArtifactVersion(code, description || 'Saved');
      },
      analyzeRiskInChat,
    }),
    [createArtifactVersion, analyzeRiskInChat]
  );
  
  // Promote current version to new major (v2, v3...) — only when it is current and not already major (v1.1 → v2, not v1 → v2)
  const promoteCurrentToMajor = (versionId: string) => {
    const version = artifactVersions.find(v => v.id === versionId);
    if (!version || !version.isCurrent) return;
    if (!version.alias.includes('.')) return;
    createArtifactVersion(version.code, 'Major branch');
    showSuccessToast(`Promoted to new major version`);
  };

  // Restore artifact version
  const restoreArtifactVersion = (versionId: string) => {
    const version = artifactVersions.find(v => v.id === versionId);
    if (!version) return;
    
    // Mark this version as current
    const updatedVersions = artifactVersions.map(v => ({
      ...v,
      isCurrent: v.id === versionId
    }));
    
    setArtifactVersions(updatedVersions);
    setCurrentVersionId(versionId);
    
    // Update edited script
    setEditedScript(version.code);
    setStreamingCode(version.code);
    
    // If there's incomplete code, update it
    if (incompleteCode) {
      setIncompleteCode({
        ...incompleteCode,
        code: version.code
      });
    }
    
    showSuccessToast(`Restored to version ${version.alias}`);
  };

  const handleApply = () => {
    if (editedScript) {
      // Create new artifact version before applying
      const newVersion = createArtifactVersion(editedScript, 'Applied changes');
      
      onScriptUpdated(editedScript);
      setEditedScript('');
      setEditRequest('');
      showSuccessToast(`Script updated in editor (saved as version ${newVersion.alias})`);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(editedScript);
    showSuccessToast('Code copied to clipboard');
  };

  const handleDiscard = () => {
    setEditedScript('');
    setEditRequest('');
    setIncompleteCode(null);
    autoContinueAttemptedRef.current = false;
    showSuccessToast('Changes discarded');
  };

  const handleContinueCode = async () => {
    if (!incompleteCode) return;

    if (!isConfigured) {
      showErrorToast('Please configure your AI API key first');
      handleConfigure();
      return;
    }

    const baseScriptForDiff = editBaseScriptRef.current ?? currentScript;

    // Script already ends with output (e.g. "output stats" + comments/decorations) — treat as complete
    if (isScriptLikelyComplete(incompleteCode.code).complete) {
      setIncompleteCode(null);
      autoContinueAttemptedRef.current = false;
      const finalCodeToUse = incompleteCode.code.trim();
      setEditedScript(finalCodeToUse);
      setStreamingCode(finalCodeToUse);
      setIsStreamingCode(false);
      const newVersion = createArtifactVersion(finalCodeToUse, 'Script already complete');
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `# Script complete (${newVersion.alias}): no continuation needed — script already ends with output.`,
          timestamp: new Date()
        }
      ]);
      showSuccessToast('Script already complete.');
      return;
    }

    setIsContinuingCode(true);
    setIsStreamingCode(true);
    // Use the preserved temporary code state as the base for continuation
    const baseCode = incompleteCode.code;
    setStreamingCode(baseCode);
    console.log('Continuing from temporary code state:', {
      baseCodeLength: baseCode.length,
      preview: baseCode.substring(0, 100)
    });

    const originalScriptHasOutput = /\boutput\s+(table|stats|signals|stats\s+by)/m.test(baseScriptForDiff);
    const originalLineCount = baseScriptForDiff.split('\n').length;

    const continuationPrompt = generateContinuationPrompt(
      incompleteCode.code,
      originalLineCount,
      originalScriptHasOutput
    );

    const codeSystemPrompt = `You are an expert QuantQuack DSL developer. Your ONLY task is to output the missing continuation of a DSL script. Rules: 1) Output ONLY the remaining lines (from the next line after the snippet the user will show to the end). 2) Do NOT repeat the beginning. 3) The last line of your response MUST be "output table", "output stats", or "output signals". 4) Raw DSL only, no markdown, no explanations.`;

    try {
      // Use the same generateCodeWithStreaming function
      const { getAIConfig } = await import('@/services/ai/ai-config');
      const config = getAIConfig();
      if (!config) {
        throw new Error('AI configuration not found');
      }

      const generateCodeWithStreaming = async (prompt: string, systemPrompt: string): Promise<{ code: string; streamingState: string; endedDueToTokens: boolean }> => {
        return new Promise((resolve, reject) => {
          const wsService = getAIWebSocketService();
          const codeChatId = `code-cont-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          let accumulatedCode = '';
          let codeCleanup: (() => void) | null = null;
          let lastStreamingState = ''; // Track the last streaming state

          wsService.connect(codeChatId, (message) => {
            if (editAbortControllerRef.current?.signal.aborted) {
              if (codeCleanup) codeCleanup();
              reject(new Error('Operation aborted'));
              return;
            }

            if (message.type === 'chunk' && message.content) {
              accumulatedCode += message.content;
              let cleanedStreaming = accumulatedCode.trim();
              if (cleanedStreaming.startsWith('```')) {
                cleanedStreaming = cleanedStreaming.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
              }
              // Append continuation to existing incomplete code
              const fullStreamingCode = incompleteCode.code + '\n' + cleanedStreaming;
              lastStreamingState = fullStreamingCode;
              setStreamingCode(fullStreamingCode);
              
              setTimeout(() => {
                chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 50);
            } else if (message.type === 'done') {
              setIsStreamingCode(false);
              
              // Enhanced final cleanup
              let finalCode = accumulatedCode.trim();
              if (finalCode.startsWith('```')) {
                finalCode = finalCode.replace(/^```[\w]*\n?/gm, '').replace(/\n?```$/gm, '');
              }
              const codeBlockMatch = finalCode.match(/```[\w]*\n?([\s\S]*?)\n?```/);
              if (codeBlockMatch) {
                finalCode = codeBlockMatch[1].trim();
              }
              finalCode = finalCode.split(/```[\w]*/).pop() || finalCode;
              finalCode = finalCode.split(/```/).shift() || finalCode;
              finalCode = finalCode.trim();
              
              // Merge continuation with base code
              const mergedCode = incompleteCode.code.trim() + '\n' + finalCode.trim();
              
              // Check if stream ended due to token limit
              const endedDueToTokens = message.finishReason === 'length';
              
              // Preserve the streaming state - use the last streaming state or merged code
              const preservedState = lastStreamingState || mergedCode;
              
              if (codeCleanup) codeCleanup();
              resolve({ 
                code: mergedCode, 
                streamingState: preservedState,
                endedDueToTokens 
              });
            } else if (message.type === 'error') {
              setIsStreamingCode(false);
              if (codeCleanup) codeCleanup();
              reject(new Error(message.error || 'Streaming error'));
            }
          }).then((cleanup) => {
            codeCleanup = cleanup;

            const sendRequest = () => {
              if (wsService.isConnected(codeChatId)) {
                const success = wsService.sendChatRequest(codeChatId, {
                  provider: config.provider,
                  apiKey: config.apiKey,
                  messages: [
                    { role: 'system', content: systemPrompt },
                    { role: 'user', content: prompt }
                  ],
                  model: config.model,
                  temperature: 0.3,
                  maxTokens: 6000
                });

                if (!success) {
                  setIsStreamingCode(false);
                  if (codeCleanup) codeCleanup();
                  reject(new Error('Failed to send streaming request'));
                }
              } else {
                setTimeout(sendRequest, 100);
              }
            };

            setTimeout(sendRequest, 200);
          }).catch((err) => {
            setIsStreamingCode(false);
            if (codeCleanup) codeCleanup();
            reject(err);
          });
        });
      };

      const result = await generateCodeWithStreaming(continuationPrompt, codeSystemPrompt);
      const continuationCode = result.code; // This already includes the merged code
      const preservedStreamingState = result.streamingState || continuationCode;
      const continuationEndedDueToTokens = result.endedDueToTokens;
      const mergedCode = continuationCode; // Already merged in generateCodeWithStreaming
      
      // Update streaming code with preserved state
      setStreamingCode(preservedStreamingState);

      // Check if still incomplete (using diff analysis)
      const mergedLines = mergedCode.split('\n');
      const diff = diffLines(baseScriptForDiff, mergedCode);
      
      // Analyze diff for truncation
      let removedLinesAtEnd = 0;
      let consecutiveRemovedAtEnd = 0;
      let lastOperationWasRemoved = false;
      
      for (let i = diff.length - 1; i >= 0; i--) {
        const part = diff[i];
        if (part.removed) {
          consecutiveRemovedAtEnd += part.count || 0;
          removedLinesAtEnd += part.count || 0;
          lastOperationWasRemoved = true;
        } else if (part.added || (!part.added && !part.removed)) {
          if (lastOperationWasRemoved) break;
        }
      }
      
      const hasOutput = /\boutput\s+(table|stats|signals|stats\s+by)/m.test(mergedCode);
      const newLineCount = mergedLines.filter(line => line.trim().length > 0).length;
      const lineCountRatio = newLineCount / originalLineCount;
      const stillShorter = lineCountRatio < 0.75;
      const significantRemovalAtEnd = consecutiveRemovedAtEnd > 5 || 
                                      (consecutiveRemovedAtEnd > 0 && lineCountRatio < 0.8);
      
      // If continuation also ended due to tokens, definitely incomplete
      const isStillIncomplete = continuationEndedDueToTokens ||
                               (!hasOutput && originalScriptHasOutput) || 
                               stillShorter ||
                               significantRemovalAtEnd;

      if (isStillIncomplete) {
        // Still incomplete - update state and show Continue button again
        const reasons: string[] = [];
        if (continuationEndedDueToTokens) {
          reasons.push('stream ended due to token limit');
        }
        if (!hasOutput && originalScriptHasOutput) {
          reasons.push('missing output statement');
        }
        if (stillShorter) {
          reasons.push(`too short (${newLineCount} vs ${originalLineCount} lines)`);
        }
        if (significantRemovalAtEnd) {
          reasons.push(`truncated at end (${consecutiveRemovedAtEnd} lines removed)`);
        }
        
        // Use preserved streaming state for incomplete code
        const codeToPreserve = preservedStreamingState.trim() || mergedCode;
        setIncompleteCode({
          code: codeToPreserve,
          needsOutput: !hasOutput && originalScriptHasOutput,
          isShorter: stillShorter || significantRemovalAtEnd
        });
        setStreamingCode(codeToPreserve);
        
        const stillIncompleteMessage: ChatMessage = {
          role: 'assistant',
          content: `**Code Continued (Still Incomplete):**\n\n\`\`\`dsl\n${mergedCode}\n\`\`\`\n\n⚠️ Code generation still appears incomplete: ${reasons.join(', ')}. Click "Continue" again to generate more.`,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, stillIncompleteMessage]);
      } else {
        // Code is now complete
        setIncompleteCode(null);
        autoContinueAttemptedRef.current = false;
        
        // Use preserved streaming state for final code
        const finalCodeToUse = preservedStreamingState.trim() || mergedCode;
        
        // Create artifact version for completed continuation
        const newVersion = createArtifactVersion(finalCodeToUse, 'Code continuation completed');
        
        const completeMessage: ChatMessage = {
          role: 'assistant',
          content: `**Generated Code (Complete - ${newVersion.alias}):**\n\n\`\`\`dsl\n${finalCodeToUse}\n\`\`\``,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, completeMessage]);
        showSuccessToast(`Code generation completed! (saved as version ${newVersion.alias})`);
        
        setEditedScript(finalCodeToUse);
        setStreamingCode(finalCodeToUse);
        setIsStreamingCode(false);
      }
    } catch (err) {
      console.error('Error continuing code:', err);
      showErrorToast('Failed to continue code generation. Please try again.');
      setIsStreamingCode(false);
    } finally {
      setIsContinuingCode(false);
    }
  };

  const quickEditOptions = [
    { label: 'Add error handling', request: 'Add error handling and validation to the script' },
    { label: 'Optimize performance', request: 'Optimize the script for better performance' },
    { label: 'Add comments', request: 'Add helpful comments explaining the logic' },
    { label: 'Fix syntax errors', request: 'Fix any syntax errors and improve code quality' },
    { label: 'Simplify code', request: 'Simplify the code while maintaining functionality' },
    { label: 'Add more signals', request: 'Add additional trading signals based on the existing pattern' }
  ];

  const handleQuickEdit = async (request: string) => {
    if (!isConfigured) {
      showErrorToast(null, 'Please configure your AI API key first');
      handleConfigure();
      return;
    }

    if (!currentScript.trim()) {
      showErrorToast(null, 'No script loaded. Please load a script first.');
      return;
    }

    setEditRequest(request);
    try {
      await handleEdit({ request });
    } catch (err) {
      console.error('Error in quick edit:', err);
      // Error is already shown by handleEdit
    }
  };

  const handleCompileAndFix = async () => {
    if (!isConfigured) {
      showErrorToast(null, 'Please configure your AI API key first');
      handleConfigure();
      return;
    }
    if (!currentScript.trim()) {
      showErrorToast(null, 'No script to compile. Load a script first.');
      return;
    }
    setIsFixingCompilation(true);
    try {
      const result = await compileAndFix(
        currentScript,
        (prompt, systemPrompt, opts) => callAI(prompt, systemPrompt, opts).then((r) => r ?? null),
        { maxRetries: 2 }
      );
      onScriptUpdated(result.code);
      if (result.success) {
        showSuccessToast(
          result.rounds === 0
            ? 'Script compiles successfully.'
            : `Compilation errors fixed in ${result.rounds} round(s).`
        );
      } else {
        const msg = result.errors?.length
          ? `Could not fix all errors after ${result.rounds} attempt(s). ${result.errors.length} error(s) remain.`
          : 'Compilation failed. Check the script.';
        showErrorToast(msg);
      }
    } catch (err) {
      console.error('Compile-and-fix error:', err);
      showErrorToast(err instanceof Error ? err.message : 'Failed to compile and fix.');
    } finally {
      setIsFixingCompilation(false);
    }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim()) {
      return;
    }

    if (!isConfigured) {
      showErrorToast('Please configure your AI API key to chat');
      handleConfigure();
      return;
    }

    const userMessage = chatInput.trim();
    setChatInput('');
    
    // Check if user wants to continue incomplete code
    const isContinueCommand = /^\s*continue\s*$/i.test(userMessage);
    const hasIncompleteCode = incompleteCode !== null;
    const hasStreamingCode = isStreamingCode && streamingCode.trim().length > 0;
    
    if (isContinueCommand && (hasIncompleteCode || hasStreamingCode)) {
      // User wants to continue incomplete code - trigger continuation
      if (hasIncompleteCode) {
        // Use the existing handleContinueCode function
        await handleContinueCode();
      } else if (hasStreamingCode) {
        // Code is still streaming - preserve current state and continue
        const currentStreamingCode = streamingCode.trim();
        if (currentStreamingCode.length > 0) {
          // Stop streaming and preserve as incomplete code
          setIsStreamingCode(false);
          
          // Check if code looks incomplete (basic heuristics)
          const hasOutput = /\boutput\s+(table|stats|signals|stats\s+by)/m.test(currentStreamingCode);
          const lineCount = currentStreamingCode.split('\n').length;
          const originalLineCount = currentScript.split('\n').length;
          const isShorter = lineCount < originalLineCount * 0.75;
          
          // Set as incomplete code
          setIncompleteCode({
            code: currentStreamingCode,
            needsOutput: !hasOutput,
            isShorter: isShorter
          });
          
          // Add user message to chat
          const continueMessage: ChatMessage = {
            role: 'user',
            content: 'continue',
            timestamp: new Date()
          };
          setChatMessages(prev => [...prev, continueMessage]);
          
          // Wait a bit for state to update, then continue
          setTimeout(async () => {
            await handleContinueCode();
          }, 200);
        }
      }
      return; // Don't send as regular chat message
    }

    if (!currentScript.trim()) {
      showErrorToast('No script loaded. Please load a script first.');
      return;
    }

    const lastAssistantCode = getLastAssistantCode(chatMessages);
    const hasScriptInEditor = !!currentScript.trim();
    const hasLastMessageWithCode = !!lastAssistantCode;

    // 1) Por defecto: detección por palabras clave (sin llamar a la IA)
    let intent = detectUserIntent(userMessage, hasScriptInEditor);
    let editTarget: EditTarget | 'none' = detectEditTarget(userMessage, hasLastMessageWithCode);

    // 2) Solo si las palabras clave no dieron una intención clara → llamar a la IA para clasificar
    if (intent === 'unknown' || intent === 'general_question') {
      try {
        const { prompt: classifyPrompt, systemPrompt: classifySystem } =
          buildIntentClassificationPrompt(userMessage, hasScriptInEditor, hasLastMessageWithCode);
        const classifyResponse = await callAI(classifyPrompt, classifySystem, {
          temperature: 0.1,
          maxTokens: 120
        });
        if (classifyResponse) {
          const parsed = parseIntentClassificationResponse(classifyResponse);
          intent = parsed.intent;
          editTarget = parsed.editTarget;
        }
      } catch {
        // Mantener intent y editTarget de palabras clave
      }
    }

    const newUserMessage: ChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
      intent
    };
    setChatMessages(prev => [...prev, newUserMessage]);
    setIsChatLoading(true);
    setIsStreaming(true);
    setStreamingContent('');

    const isEditIntent = intent === 'code_improvement' || intent === 'error_fix';
    const useEditTarget = isEditIntent && editTarget !== 'none';
    if (useEditTarget) {
      const baseScript =
        editTarget === 'last_message' && lastAssistantCode ? lastAssistantCode : currentScript;
      if (baseScript.trim()) {
        setIsChatLoading(false);
        setIsStreaming(false);
        setStreamingContent('');
        await handleEdit({ baseScript, request: userMessage, skipAddUserMessage: true });
        return;
      }
    }

    // Create context for nested prompts
    const scriptContext = {
      results: {},
      scriptDescription: `DSL script with ${currentScript.split('\n').length} lines`,
      scriptCode: currentScript,
      datasetInfo: datasetInfo ? {
        symbol: datasetInfo.symbol,
        timeframe: datasetInfo.timeframe,
        columns: datasetInfo.columns
      } : undefined,
      resultType: undefined as any
    };

    const nestedPrompts = buildNestedPrompts(
      scriptContext,
      chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      } as AIMessage))
    );

    // Detect frustration to add warm, humanized response instructions
    const isFrustrated = detectUserFrustration(userMessage, {
      hasCompilationErrors: compilationInfo?.hasErrors,
      lastAssistantWasError: (() => {
        const last = chatMessages.filter((m) => m.role === 'assistant').pop();
        if (!last?.content) return false;
        const c = last.content.toLowerCase();
        return c.includes('error') && (c.includes('compilation') || c.includes('failed') || c.includes('couldn\'t'));
      })(),
    });
    const warmInstructionsBlock = isFrustrated ? `\n\n${getWarmResponseInstructions()}` : '';

    // Build system prompt with full DSL reference so the assistant follows correct syntax
    const systemPrompt = `You are an expert QuantQuack DSL developer assistant. Help the user understand and improve their DSL script.
${warmInstructionsBlock}

${existingSignals && existingSignals.length > 0 ? `**Existing Signals**: ${existingSignals.join(', ')}\n` : ''}
${datasetInfo ? `**Dataset**: ${datasetInfo.symbol || 'N/A'} ${datasetInfo.timeframe || ''}${datasetInfo.columns?.length ? ` | Columns: ${datasetInfo.columns.join(', ')}` : ''}\n` : ''}
${compilationInfo?.hasErrors ? `**Status**: Compilation errors detected. Use the DSL reference below to fix them (e.g. if then else, input literals only, config/output present).\n` : ''}

When suggesting or generating code, you MUST follow the DSL syntax reference below. Common fixes: use "if cond then a else b" not "? :"; input values must be literals; include config: at top and one output at end; use "stats by date:" or "stats by session:" with count(...) as x, percent(...) as x, etc.

${getDSLSyntaxReference()}

Provide helpful, clear explanations and suggestions. If the user asks to modify the script, provide the complete updated code that respects the reference above.`;

    // Create optimized messages with nested prompts
    const optimizedMessages = createNestedPrompt(
      nestedPrompts,
      systemPrompt,
      userMessage
    );

    // Get AI config
    const { getAIConfig } = await import('@/services/ai/ai-config');
    const config = getAIConfig();
    
    if (!config) {
      showErrorToast('AI configuration not found');
      setIsChatLoading(false);
      setIsStreaming(false);
      return;
    }

    // Use WebSocket streaming if available, otherwise fallback to HTTP
    try {
      const wsService = getAIWebSocketService();
      const chatId = chatIdRef.current;

      // Cleanup previous connection if exists
      if (wsCleanupRef.current) {
        wsCleanupRef.current();
      }

      // Connect to WebSocket and send request
      let fullResponse = '';
      
      wsService.connect(chatId, (message) => {
        if (message.type === 'chunk' && message.content) {
          fullResponse += message.content;
          setStreamingContent(fullResponse);
          // Auto-scroll to bottom when new content arrives
          setTimeout(() => {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 50);
        } else if (message.type === 'done') {
          setIsStreaming(false);
          setIsChatLoading(false);
          
          // Always add response if we have content, even if empty
          if (fullResponse.trim()) {
            const assistantMessage: ChatMessage = {
              role: 'assistant',
              content: fullResponse.trim(),
              timestamp: new Date()
            };
            setChatMessages(prev => [...prev, assistantMessage]);
          }
          setStreamingContent('');
          fullResponse = '';
        } else if (message.type === 'error') {
          setIsStreaming(false);
          setIsChatLoading(false);
          setStreamingContent('');
          const streamError = new Error(message.error || 'Streaming error');
          showErrorToast(streamError, 'AI streaming error');
        }
      }).then((cleanup) => {
        wsCleanupRef.current = cleanup;

        // Wait for WebSocket to be ready, then send request
        let retryCount = 0;
        const maxRetries = 15; // 15 retries * 200ms = 3 seconds max
        
        const checkAndSend = () => {
          retryCount++;
          
          if (wsService.isConnected(chatId)) {
            const sent = wsService.sendChatRequest(chatId, {
              provider: config.provider,
              apiKey: config.apiKey,
              messages: optimizedMessages,
              model: config.model,
              temperature: 0.7,
              maxTokens: 2000
            });

            if (!sent) {
              // Fallback to HTTP if send failed
              handleChatSendHTTP(optimizedMessages);
            }
          } else if (retryCount < maxRetries) {
            // Retry after a short delay
            setTimeout(checkAndSend, 200);
          } else {
            // Max retries reached, fallback to HTTP
            handleChatSendHTTP(optimizedMessages);
          }
        };

        // Start checking after connection
        setTimeout(checkAndSend, 300);
      }).catch(() => {
        // Fallback to HTTP if WebSocket fails
        handleChatSendHTTP(optimizedMessages);
      });
    } catch (wsError) {
      // Fallback to HTTP
      handleChatSendHTTP(optimizedMessages);
    }
  };

  // Stop AI operation
  const handleStop = () => {
    // Stop WebSocket streaming
    if (wsCleanupRef.current) {
      const wsService = getAIWebSocketService();
      wsService.stopStreaming(chatIdRef.current);
      wsCleanupRef.current();
      wsCleanupRef.current = null;
    }

    // Abort any ongoing edit operation
    if (editAbortControllerRef.current) {
      editAbortControllerRef.current.abort();
      editAbortControllerRef.current = null;
    }

    // Reset states (this will prevent HTTP responses from being processed)
    setIsStreaming(false);
    setIsChatLoading(false);
    setIsLoading(false);
    setIsStreamingCode(false);
    setIsContinuingCode(false);
    setStreamingContent('');
    setStreamingCode('');
    setIncompleteCode(null);
  };

  // Fallback HTTP handler
  const handleChatSendHTTP = async (conversationHistory: AIMessage[]) => {
    setIsStreaming(false);
    
    try {
      const response = await callAIChat(conversationHistory, {
        temperature: 0.7,
        maxTokens: 2000
      });

      // Always add response if we got one (check state at time of response)
      if (response) {
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: response,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, assistantMessage]);
      }
    } catch (err) {
      // Only show error if not stopped
      if (isChatLoading) {
        throw err;
      }
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleChatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  };

  const clearChat = () => {
    setChatMessages([]);
  };

  const [isRunAndAnalyzing, setIsRunAndAnalyzing] = useState(false);
  const handleRunAndAnalyze = async () => {
    if (!runAndAnalyzeParams || !isConfigured) return;
    setIsRunAndAnalyzing(true);
    try {
      const outcome = await runAndAnalyze({
        scriptId: runAndAnalyzeParams.scriptId,
        datasetId: runAndAnalyzeParams.datasetId,
        symbol: runAndAnalyzeParams.symbol,
        dateStart: runAndAnalyzeParams.dateStart,
        dateEnd: runAndAnalyzeParams.dateEnd,
        scriptCode: runAndAnalyzeParams.scriptCode,
        scriptDescription: runAndAnalyzeParams.scriptDescription,
        datasetInfo: runAndAnalyzeParams.datasetInfo,
        callAI: (prompt, systemPrompt, opts) => callAI(prompt, systemPrompt, opts).then((r) => r ?? null),
      });
      if (outcome.runStatus === 'failed' && outcome.runError) {
        setChatMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `**Run failed:** ${outcome.runError}`, timestamp: new Date() },
        ]);
        showErrorToast(new Error(outcome.runError), 'Run and analyze');
        return;
      }
      if (outcome.analysisError) {
        setChatMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `**Analysis failed:** ${outcome.analysisError}`, timestamp: new Date() },
        ]);
        showErrorToast(new Error(outcome.analysisError), 'Run and analyze');
        return;
      }
      if (outcome.analysis) {
        setChatMessages((prev) => [
          ...prev,
          { role: 'assistant', content: outcome.analysis ?? '', timestamp: new Date() },
        ]);
        showSuccessToast('Run and analysis completed');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Run and analyze failed';
      showErrorToast(e instanceof Error ? e : new Error(msg), 'Run and analyze');
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `**Error:** ${msg}`, timestamp: new Date() },
      ]);
    } finally {
      setIsRunAndAnalyzing(false);
    }
  };

  // Format intent name for display
  const formatIntentName = (intent: string): string => {
    const intentMap: Record<string, string> = {
      'code_improvement': 'Code Improvement',
      'code_explanation': 'Code Explanation',
      'analysis_question': 'Analysis Question',
      'methodology_question': 'Methodology',
      'suggestion_request': 'Suggestion',
      'error_fix': 'Error Fix',
      'general_question': 'General Question',
      'code_generation': 'Code Generation',
      'learn_dsl': 'Learn DSL',
      'learn_algorithmic_trading': 'Learn Trading',
      'unknown': 'Unknown'
    };
    return intentMap[intent] || intent;
  };

  return (
    <div className="flex flex-col h-full bg-background border-l border-t overflow-hidden">
      {/* Compact Header - VSCode style */}
      <div className="h-8 flex items-center justify-between px-3 border-b bg-muted/30 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Wand2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <span className="text-xs font-semibold truncate">AI Assistant</span>
          {isConfigured && (
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
          )}
        </div>
      </div>
      
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden" style={{ minHeight: 0 }}>
        {(!isConfigured || !currentScript.trim()) && (
          <div className="p-3 border-b bg-muted/20 flex-shrink-0">
            {!isConfigured && (
              <div className="flex items-center justify-between text-xs gap-2">
                <span className="text-muted-foreground truncate">AI not configured</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleConfigure}
                  className="h-6 px-2 text-xs flex-shrink-0"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Configure
                </Button>
              </div>
            )}

            {!currentScript.trim() && (
              <div className="text-xs text-muted-foreground">
                No script loaded
              </div>
            )}
          </div>
        )}

        <Tabs defaultValue="edit" className="flex-1 flex flex-col min-h-0 w-full overflow-hidden">
          {/* Compact Tabs - VSCode style */}
          <div className="h-9 flex items-center justify-between border-b bg-muted/20 flex-shrink-0 overflow-hidden gap-2">
            <TabsList className="h-7 bg-transparent p-0 gap-0.5 flex-1 min-w-0 flex">
              <TabsTrigger 
                value="edit" 
                className="h-7 px-2 text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm flex-1 min-w-0 flex items-center justify-center"
              >
                <Wand2 className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">Edit</span>
              </TabsTrigger>
              <TabsTrigger 
                value="chat" 
                className="h-7 px-2 text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm flex-1 min-w-0 flex items-center justify-center relative"
              >
                <MessageSquare className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">Chat</span>
                {chatMessages.length > 0 && (
                  <span className="ml-1 px-1 text-[10px] bg-primary text-primary-foreground rounded-full min-w-[14px] h-3.5 flex items-center justify-center flex-shrink-0">
                    {chatMessages.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            {artifactVersions.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowVersionHistory(true)}
                className="h-7 px-2 text-xs flex-shrink-0"
                title="Version history"
              >
                <History className="w-3 h-3 mr-1" />
                <span className="truncate">{artifactVersions.find(v => v.id === currentVersionId)?.alias ?? 'v0'}</span>
              </Button>
            )}
          </div>

          <TabsContent value="edit" className="data-[state=inactive]:hidden flex-1 flex flex-col min-h-0 p-3 space-y-3 overflow-hidden">
            <div className="space-y-2 flex-shrink-0">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
                Edit Request
              </label>
              <Textarea
                placeholder='e.g., "Change RSI period to 21" or "Add moving average calculation"'
                value={editRequest}
                onChange={(e) => setEditRequest(e.target.value)}
                rows={3}
                className="resize-none text-xs font-mono w-full"
              />
            </div>

            <div className="space-y-2 flex-shrink-0">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
                Quick Actions
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCompileAndFix}
                disabled={isFixingCompilation || isLoading || !currentScript.trim() || !isConfigured}
                className="w-full justify-start h-auto py-1.5 px-2 text-xs mb-1.5 border-primary/50 hover:bg-primary/10"
              >
                {isFixingCompilation ? (
                  <Loader2 className="w-3 h-3 mr-1.5 flex-shrink-0 animate-spin text-muted-foreground" />
                ) : (
                  <Check className="w-3 h-3 mr-1.5 flex-shrink-0 text-muted-foreground" />
                )}
                <span className="truncate text-left">Compile & fix with AI</span>
              </Button>
              <div className="grid grid-cols-2 gap-1.5">
                {quickEditOptions.map((option, index) => (
                  <Button
                    key={index}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleQuickEdit(option.request)}
                    disabled={isLoading || !currentScript.trim() || !isConfigured}
                    className="w-full justify-start h-auto py-1.5 px-2 text-xs hover:bg-muted"
                  >
                    <ArrowRight className="w-3 h-3 mr-1.5 flex-shrink-0 text-muted-foreground" />
                    <span className="truncate text-left">{option.label}</span>
                  </Button>
                ))}
              </div>
            </div>


            {isLoading ? (
              <div className="flex gap-2">
                <Button
                  onClick={handleStop}
                  variant="destructive"
                  className="flex-1 h-8 text-xs flex-shrink-0"
                >
                  <Square className="w-3 h-3 mr-2 flex-shrink-0" />
                  <span className="truncate">Stop</span>
                </Button>
                <Button
                  disabled
                  className="flex-1 h-8 text-xs flex-shrink-0"
                  variant="outline"
                >
                  <Loader2 className="w-3 h-3 mr-2 animate-spin flex-shrink-0" />
                  <span className="truncate">
                    {isReasoning ? 'Analyzing Request...' : isProposingChanges ? 'Proposing Changes...' : 'Generating Code...'}
                  </span>
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => handleEdit()}
                disabled={isLoading || !editRequest.trim() || !currentScript.trim()}
                className="w-full h-8 text-xs flex-shrink-0"
                variant={!isConfigured ? "outline" : "default"}
              >
                {!isConfigured ? (
                  <>
                    <Settings className="w-3 h-3 mr-2 flex-shrink-0" />
                    <span className="truncate">Configure</span>
                  </>
                ) : (
                  <>
                    <Wand2 className="w-3 h-3 mr-2 flex-shrink-0" />
                    <span className="truncate">Edit Script</span>
                  </>
                )}
              </Button>
            )}
          </TabsContent>

          <TabsContent value="chat" className="data-[state=inactive]:hidden flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Chat header: Run and analyze (when params provided), Clear chat */}
            <div className="flex shrink-0 items-center justify-end gap-1 py-2 px-3 border-b border-border/50">
              {runAndAnalyzeParams && runAndAnalyzeParams.scriptId && runAndAnalyzeParams.datasetId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRunAndAnalyze}
                  disabled={isRunAndAnalyzing || !isConfigured}
                  className="h-7 px-3 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                  title={!isConfigured ? 'Configure AI in Settings' : 'Run script on dataset and append AI analysis to chat'}
                >
                  {isRunAndAnalyzing ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Play className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Run and analyze
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearChat}
                disabled={chatMessages.length === 0}
                className="h-7 px-3 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                title={chatMessages.length === 0 ? undefined : 'Limpiar conversación (se borra también del historial de esta sesión)'}
              >
                <X className="w-3.5 h-3.5 mr-1.5" />
                Limpiar chat
              </Button>
            </div>
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto px-4 min-h-0" style={{ minHeight: 0, overflowY: 'auto' }}>
              {chatMessages.length === 0 && !isLoading && !isReasoning && !isProposingChanges && !isChatLoading ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <div className="text-center space-y-1">
                    <MessageSquare className="w-6 h-6 mx-auto opacity-40" />
                    <p className="text-xs">Start a conversation</p>
                  </div>
                </div>
              ) : (
                <>
                  {chatMessages.map((message, index) => {
                    // Enhanced code detection: check for code blocks and DSL patterns
                    
                    // Try to match complete code blocks first
                    let codeMatch = message.content.match(/```[\w]*\n?([\s\S]*?)\n?```/);
                    let extractedCode = codeMatch ? codeMatch[1] : null;
                    
                    // If no complete code block, try to detect DSL code patterns
                    if (!extractedCode && message.role === 'assistant') {
                      // Look for DSL patterns: input, signal, stats, etc.
                      const dslPatterns = [
                        /(?:^|\n)(input\s+\w+\s*=)/m,
                        /(?:^|\n)(signal\s+\w+)/m,
                        /(?:^|\n)(stats\s*:)/m,
                        /(?:^|\n)(#\s+[A-Z])/m, // Comments starting with #
                      ];
                      
                      const hasDSLPattern = dslPatterns.some(pattern => pattern.test(message.content));
                      
                      if (hasDSLPattern) {
                        // Try to extract code from incomplete blocks or find code-like content
                        // Look for content after "```" even if closing ``` is missing
                        const incompleteBlock = message.content.match(/```[\w]*\n?([\s\S]*?)(?:\n?```|$)/);
                        if (incompleteBlock) {
                          extractedCode = incompleteBlock[1].trim();
                        } else {
                          // If no backticks at all, try to find code-like sections
                          // Look for lines that look like DSL code (containing keywords)
                          const lines = message.content.split('\n');
                          const codeStartIndex = lines.findIndex(line => 
                            /^\s*(input|signal|stats|#\s+[A-Z])/.test(line)
                          );
                          
                          if (codeStartIndex !== -1) {
                            // Extract from code start to end or next markdown section
                            const codeEndIndex = lines.findIndex((line, idx) => 
                              idx > codeStartIndex && 
                              (line.trim().startsWith('##') || line.trim().startsWith('**') || line.trim() === '')
                            );
                            
                            const codeLines = codeEndIndex !== -1 
                              ? lines.slice(codeStartIndex, codeEndIndex)
                              : lines.slice(codeStartIndex);
                            
                            extractedCode = codeLines.join('\n').trim();
                          }
                        }
                      }
                    }
                    if (message.type === 'code') extractedCode = message.content;
                    const hasCode = !!extractedCode;

                    if (message.role === 'assistant' && message.type === 'reasoning') {
                      return (
                        <div key={index} className={`flex gap-3 py-4 flex-row ${index > 0 ? 'border-t border-border/50' : ''}`}>
                          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/50 text-muted-foreground">
                            <Bot className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              <span className="text-xs text-muted-foreground font-semibold">Analysis Plan</span>
                            </div>
                            <div className="text-sm bg-background/50 p-3 rounded border">
                              <MarkdownRenderer content={message.content} className="text-sm" />
                            </div>
                            {message.nextExplorations && message.nextExplorations.length > 0 && (
                              <div className="mt-3 pt-2 border-t border-border">
                                <p className="text-xs text-muted-foreground font-medium mb-2">Next explorations</p>
                                <div className="flex flex-wrap gap-2">
                                  {message.nextExplorations.map((idea, i) => (
                                    <Button
                                      key={i}
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs font-normal"
                                      onClick={() => setChatInput(`NEW EXPLORATION: ${idea}`)}
                                    >
                                      <Compass className="w-3 h-3 mr-1.5" />
                                      {idea}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }

                    if (message.role === 'user' && (message as ChatMessage).type === 'risk_analysis' && (message as ChatMessage).riskAnalysis) {
                      const risk = (message as ChatMessage).riskAnalysis as SignalRiskAnalysis;
                      const ctx = (message as ChatMessage).riskAnalysisContext;
                      const meta = risk.metadata;
                      const best = risk.ranking?.bestSignal;
                      const top = risk.ranking?.signalRanking?.slice(0, 5) ?? [];
                      const evalLabel = ctx
                        ? ctx.evaluationMode === 'hold'
                          ? `Hold (${ctx.simulationBars ?? meta.simulationBars ?? 'N'} bars)`
                          : ctx.evaluationMode === 'day_close'
                            ? 'Day Close'
                            : ctx.evaluationMode === 'session_close'
                              ? 'Session Close'
                              : ctx.evaluationMode
                        : null;
                      const nextMessage = chatMessages[index + 1];
                      const hasAnalysisReply = nextMessage?.role === 'assistant';
                      return (
                        <div key={index} className={`flex gap-3 py-4 flex-row-reverse ${index > 0 ? 'border-t border-border/50' : ''}`}>
                          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary">
                            <User className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0 max-w-[85%]">
                            <div className="rounded-lg border bg-card p-3 shadow-sm">
                              <div className="flex items-center gap-2 mb-2">
                                <BarChart3 className="w-4 h-4 text-primary" />
                                <span className="text-sm font-semibold">{t('signals.riskSignalsAnalysis')}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mb-2">
                                {t('signals.signalsCount', { count: meta.signalsAnalyzed })} · {t('signals.confidence').toLowerCase()} {meta.confidence}
                                {evalLabel && ` · ${t('signals.evaluation')} ${evalLabel}`}
                                {best?.name && ` · Best: ${best.name}`}
                              </p>
                              {top.length > 0 && (
                                <p className="text-xs text-muted-foreground mb-3">
                                  Top: {top.map((r) => r.signalName).join(', ')}
                                </p>
                              )}
                              {!hasAnalysisReply && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1.5 h-7 text-xs"
                                  onClick={() => analyzeRiskInChat(risk, ctx)}
                                  disabled={!isConfigured || isChatLoading}
                                >
{isChatLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                                {t('signals.analyzeWithAI')}
                              </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div
                        key={index}
                        className={`flex gap-3 py-4 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'} ${
                          index > 0 ? 'border-t border-border/50' : ''
                        }`}
                      >
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          message.role === 'user' 
                            ? 'bg-primary/10 text-primary' 
                            : 'bg-muted/50 text-muted-foreground'
                        }`}>
                          {message.role === 'user' ? (
                            <User className="w-4 h-4" />
                          ) : (
                            <Bot className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 max-w-[calc(100%-3rem)]">
                          <div className="prose prose-sm dark:prose-invert max-w-none">
                            {hasCode ? (
                              <div className="space-y-3">
                                {message.content.replace(/```[\w]*\n?[\s\S]*?\n?```/g, '').trim() && 
                                 !message.content.trim().startsWith('```') && (
                                  <div className="text-sm leading-relaxed">
                                    <MarkdownRenderer 
                                      content={message.content.replace(/```[\w]*\n?[\s\S]*?\n?```/g, '').replace(/```[\w]*\n?[\s\S]*$/g, '').trim()} 
                                    />
                                  </div>
                                )}
                                <div className="relative group">
                                  <div className="bg-[#1e1e1e] dark:bg-[#0d0d0d] rounded-lg border border-border/50 overflow-hidden">
                                    <div className="flex items-center justify-between px-3 py-1.5 bg-muted/30 border-b border-border/50">
                                      <span className="text-xs text-muted-foreground font-mono">dsl</span>
                                      {message.role === 'assistant' && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={async () => {
                                            const cleanedCode = extractedCode!.trim();
                                            setCodeToUse(cleanedCode);
                                            setCodeValidationErrors([]);
                                            setShowCodeModal(true);
                                            
                                            // Validate syntax when opening modal
                                            if (cleanedCode.length > 10) {
                                              setIsValidatingCode(true);
                                              try {
                                                const response = await apiClient.post('/scripts/compile', { dslCode: cleanedCode });
                                                const compilation = response.data;
                                                
                                                if (compilation.success) {
                                                  setCodeValidationErrors([]);
                                                } else {
                                                  setCodeValidationErrors(compilation.errors || []);
                                                }
                                              } catch (err) {
                                                console.error('Syntax validation error:', err);
                                              } finally {
                                                setIsValidatingCode(false);
                                              }
                                            }
                                          }}
                                          className="h-6 px-2 text-xs hover:bg-background/50"
                                        >
                                          <Copy className="w-3 h-3 mr-1" />
                                          Use Code
                                        </Button>
                                      )}
                                    </div>
                                    <div className="p-4 overflow-x-auto">
                                      <pre className="text-xs font-mono leading-relaxed text-[#d4d4d4]">
                                        <code>{extractedCode}</code>
                                      </pre>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm leading-relaxed">
                                <MarkdownRenderer content={message.content} />
                              </div>
                            )}
                          </div>
                          {message.role === 'user' && message.intent && message.intent !== 'unknown' && message.intent !== 'general_question' && (
                            <div className="mt-2 flex justify-end">
                              <Badge variant="secondary" className="text-[10px] px-2 py-0.5 h-auto font-normal">
                                {formatIntentName(message.intent)}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {/* Show streaming content if available - AFTER all messages */}
                  {isStreaming && streamingContent && !isStreamingCode && (
                    <div className="flex gap-3 py-4 border-t border-border/50">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/50 text-muted-foreground">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0 max-w-[calc(100%-3rem)]">
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <div className="text-sm leading-relaxed">
                            <MarkdownRenderer content={streamingContent} />
                            {/* Streaming cursor indicator */}
                            <span className="inline-block w-0.5 h-4 ml-0.5 bg-primary animate-pulse align-middle" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Show streaming indicator even if no content yet - AFTER all messages */}
                  {isStreaming && !streamingContent && !isStreamingCode && (
                    <div className="flex gap-3 py-4 border-t border-border/50">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/50 text-muted-foreground">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0 flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Thinking...</span>
                      </div>
                    </div>
                  )}
                  {/* Show reasoning/proposed changes as streaming if in progress - AFTER all messages */}
                  {isReasoning && (
                    <div className="flex gap-3 py-4 border-t border-border/50">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/50 text-muted-foreground">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0 flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Analyzing request...</span>
                      </div>
                    </div>
                  )}
                  {isProposingChanges && !isReasoning && (
                    <div className="flex gap-3 py-4 border-t border-border/50">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/50 text-muted-foreground">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0 flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Proposing changes...</span>
                      </div>
                    </div>
                  )}
                  {/* Show streaming code as artifact - editable code block */}
                  {(isStreamingCode && streamingCode) || (incompleteCode && streamingCode && !isStreamingCode && !isContinuingCode) ? (
                    <div className="flex gap-3 py-4 border-t border-border/50">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/50 text-muted-foreground">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0 max-w-[calc(100%-3rem)]">
                        <div className="space-y-3">
                          {/* Artifact header */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                              <span className="text-sm font-medium">
                                {isContinuingCode ? 'Continuing code generation...' : 
                                 isStreamingCode ? 'Generating code...' : 
                                 incompleteCode ? 'Generated code (incomplete)' : 
                                 'Generated code'}
                              </span>
                              {incompleteCode && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto">
                                  Incomplete
                                </Badge>
                              )}
                              {/* Version indicator */}
                              {currentVersionId && artifactVersions.length > 0 && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 h-auto">
                                  {artifactVersions.find(v => v.id === currentVersionId)?.alias ?? 'v0'}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Version navigation: History always when any version; prev/next when 2+ */}
                              {artifactVersions.length > 0 && !isStreamingCode && (
                                <>
                                  {artifactVersions.length > 1 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const currentIndex = artifactVersions.findIndex(v => v.id === currentVersionId);
                                        if (currentIndex > 0) {
                                          restoreArtifactVersion(artifactVersions[currentIndex - 1].id);
                                        }
                                      }}
                                      disabled={artifactVersions.findIndex(v => v.id === currentVersionId) === 0}
                                      className="h-7 w-7 p-0"
                                      title="Previous version"
                                    >
                                      <ChevronLeft className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowVersionHistory(true)}
                                    className="h-7 text-xs"
                                    title="Version history"
                                  >
                                    <History className="w-3 h-3 mr-1" />
                                    {artifactVersions.find(v => v.id === currentVersionId)?.alias ?? 'v0'}
                                  </Button>
                                  {artifactVersions.length > 1 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const currentIndex = artifactVersions.findIndex(v => v.id === currentVersionId);
                                        if (currentIndex < artifactVersions.length - 1) {
                                          restoreArtifactVersion(artifactVersions[currentIndex + 1].id);
                                        }
                                      }}
                                      disabled={artifactVersions.findIndex(v => v.id === currentVersionId) === artifactVersions.length - 1}
                                      className="h-7 w-7 p-0"
                                      title="Next version"
                                    >
                                      <ChevronRight className="w-3 h-3" />
                                    </Button>
                                  )}
                                </>
                              )}
                              {!isStreamingCode && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const codeToUse = streamingCode || incompleteCode?.code || '';
                                      setCodeToUse(codeToUse);
                                      setShowCodeModal(true);
                                    }}
                                    className="h-7 text-xs"
                                  >
                                    <Copy className="w-3 h-3 mr-1" />
                                    Review
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const codeToUse = streamingCode || incompleteCode?.code || '';
                                      navigator.clipboard.writeText(codeToUse);
                                      showSuccessToast('Code copied to clipboard');
                                    }}
                                    className="h-7 text-xs"
                                  >
                                    <Copy className="w-3 h-3 mr-1" />
                                    Copy
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          {/* Artifact code block - editable */}
                          <div className="relative group border border-border/50 rounded-lg overflow-hidden bg-[#1e1e1e] dark:bg-[#0d0d0d]">
                            <div className="flex items-center justify-between px-3 py-1.5 bg-muted/30 border-b border-border/50">
                              <span className="text-xs text-muted-foreground font-mono">dsl</span>
                              <div className="flex items-center gap-2">
                                {isStreamingCode && (
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Streaming...
                                  </span>
                                )}
                                {!isStreamingCode && incompleteCode && (
                                  <span className="text-xs text-muted-foreground">Ready to continue</span>
                                )}
                              </div>
                            </div>
                            {/* Monaco Editor for better code viewing/editing - Artifact style */}
                            <div className="relative" style={{ minHeight: '200px', maxHeight: '600px' }}>
                              <MonacoEditor
                                disableMinimap={true}
                                value={streamingCode || incompleteCode?.code || ''}
                                onChange={(newValue) => {
                                  // Allow editing the code when not streaming
                                  if (!isStreamingCode && incompleteCode) {
                                    setIncompleteCode({
                                      ...incompleteCode,
                                      code: newValue || ''
                                    });
                                  }
                                  if (!isStreamingCode) {
                                    setStreamingCode(newValue || '');
                                  }
                                }}
                                language="quantquack-dsl"
                                height="400px"
                                readOnly={isStreamingCode} // Only editable when not streaming
                                onMount={(editor) => {
                                  // Configure for artifact display
                                  editor.updateOptions({
                                    minimap: { enabled: false },
                                    fontSize: 13,
                                    lineNumbers: 'on',
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true,
                                    wordWrap: 'on',
                                  });
                                  
                                  // Auto-scroll to bottom when streaming
                                  if (isStreamingCode) {
                                    const scrollToBottom = () => {
                                      const model = editor.getModel();
                                      if (model) {
                                        const lineCount = model.getLineCount();
                                        editor.setScrollTop(editor.getScrollHeight());
                                        editor.setPosition({ lineNumber: lineCount, column: 1 });
                                      }
                                    };
                                    
                                    // Watch for content changes during streaming
                                    editor.onDidChangeModelContent(() => {
                                      setTimeout(scrollToBottom, 50);
                                    });
                                    
                                    scrollToBottom();
                                  }
                                }}
                              />
                              {/* Streaming indicator overlay */}
                              {isStreamingCode && (
                                <div className="absolute bottom-2 right-2 flex items-center gap-2 text-xs text-muted-foreground bg-background/90 backdrop-blur-sm px-2 py-1 rounded border border-border/50">
                                  <Loader2 className="w-3 h-3 animate-spin text-primary" />
                                  <span>Generating code...</span>
                                </div>
                              )}
                              {/* Incomplete indicator */}
                              {!isStreamingCode && incompleteCode && (
                                <div className="absolute bottom-2 right-2 flex items-center gap-2 text-xs text-muted-foreground bg-background/90 backdrop-blur-sm px-2 py-1 rounded border border-border/50">
                                  <span className="w-2 h-2 rounded-full bg-yellow-500" />
                                  <span>Code incomplete - ready to continue</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {/* Show Continue button when code is incomplete */}
                  {incompleteCode && !isStreamingCode && !isContinuingCode && !isLoading && (
                    <div className="flex gap-3 py-4 border-t border-border/50">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/50 text-muted-foreground">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0 max-w-[calc(100%-3rem)]">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">⚠️ Code generation incomplete</span>
                            {incompleteCode.needsOutput && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto">
                                Missing output
                              </Badge>
                            )}
                            {incompleteCode.isShorter && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto">
                                Too short
                              </Badge>
                            )}
                          </div>
                          <Button
                            onClick={handleContinueCode}
                            disabled={!isConfigured}
                            className="w-full bg-primary hover:bg-primary/90"
                          >
                            <ArrowRight className="w-4 h-4 mr-2" />
                            Continue Code Generation
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  {isLoading && !isReasoning && !isProposingChanges && !isStreamingCode && (
                    <div className="flex gap-3 py-4 border-t border-border/50">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/50 text-muted-foreground">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0 flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Generating code...</span>
                      </div>
                    </div>
                  )}
                  {isChatLoading && !isStreaming && !isLoading && !isStreamingCode && (
                    <div className="flex gap-3 py-4 border-t border-border/50">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-muted/50 text-muted-foreground">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0 flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Thinking...</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </>
              )}
            </div>

            {/* Chat Input - Cursor style */}
            <div className="border-t border-border/50 bg-background flex-shrink-0">
              <div className="p-4">
                <div className="flex gap-3 items-end">
                  <div className="flex-1 min-w-0">
                    <Textarea
                      ref={chatTextareaRef}
                      placeholder="Ask about your script or request changes..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={handleChatKeyDown}
                      rows={1}
                      className="resize-none flex-1 text-sm min-w-0 min-h-[44px] max-h-[200px] py-3 px-4 rounded-lg border-border/50 bg-background focus-visible:ring-2 focus-visible:ring-primary/20"
                      disabled={isChatLoading || isStreaming || isStreamingCode || !isConfigured || !currentScript.trim()}
                      style={{ 
                        height: 'auto',
                        lineHeight: '1.5'
                      }}
                    />
                  </div>
                  {(isChatLoading || isStreaming || isStreamingCode) ? (
                    <Button
                      onClick={handleStop}
                      variant="ghost"
                      className="h-11 w-11 p-0 flex-shrink-0 rounded-lg hover:bg-destructive/10 hover:text-destructive"
                      size="sm"
                    >
                      <Square className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleChatSend}
                      disabled={isChatLoading || !chatInput.trim() || !isConfigured || !currentScript.trim()}
                      className="h-11 w-11 p-0 flex-shrink-0 rounded-lg bg-primary hover:bg-primary/90"
                      size="sm"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2 px-1">
                  Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Enter</kbd> to send, <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Shift+Enter</kbd> for new line
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {error && (
          <div className="p-2 border-t bg-destructive/10 flex-shrink-0">
            <p className="text-xs text-destructive truncate">{error.message}</p>
          </div>
        )}

        {editedScript && (() => {
          const diff = diffLines(currentScript, editedScript);
          const hasChanges = diff.some(part => part.added || part.removed);
          
          return (
            <div className="border-t bg-muted/10 flex flex-col min-h-0 flex-shrink-0" style={{ maxHeight: '40%' }}>
              {/* Diff Header */}
              <div className="h-8 flex items-center justify-between px-3 border-b bg-muted/20 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  <GitCompare className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <span className="text-xs font-semibold truncate">Changes</span>
                  {hasChanges && (
                    <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                      <span className="px-1.5 py-0.5 text-[10px] bg-green-500/20 text-green-700 dark:text-green-400 rounded whitespace-nowrap">
                        +{diff.filter(p => p.added).reduce((sum, p) => sum + (p.count || 0), 0)}
                      </span>
                      <span className="px-1.5 py-0.5 text-[10px] bg-red-500/20 text-red-700 dark:text-red-400 rounded whitespace-nowrap">
                        -{diff.filter(p => p.removed).reduce((sum, p) => sum + (p.count || 0), 0)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0 items-center">
                  {artifactVersions.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowVersionHistory(true)}
                      className="h-6 px-2 text-xs"
                      title="Version history"
                    >
                      <History className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="hidden sm:inline">Versions ({artifactVersions.length})</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="h-6 px-2 text-xs"
                  >
                    <Copy className="w-3 h-3 mr-1 flex-shrink-0" />
                    <span className="hidden sm:inline">Copy</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDiscard}
                    className="h-6 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <X className="w-3 h-3 mr-1 flex-shrink-0" />
                    <span className="hidden sm:inline">Discard</span>
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleApply}
                    className="h-6 px-2 text-xs"
                  >
                    Apply
                  </Button>
                </div>
              </div>
            
              <Tabs defaultValue="diff" className="flex-1 flex flex-col min-h-0 w-full overflow-hidden">
                <div className="h-7 flex items-center border-b bg-muted/10 px-2 flex-shrink-0">
                  <TabsList className="h-6 bg-transparent p-0 gap-1">
                    <TabsTrigger value="diff" className="h-6 px-2 text-xs data-[state=active]:bg-background flex-shrink-0">Diff</TabsTrigger>
                    <TabsTrigger value="edited" className="h-6 px-2 text-xs data-[state=active]:bg-background flex-shrink-0">Edited</TabsTrigger>
                    <TabsTrigger value="original" className="h-6 px-2 text-xs data-[state=active]:bg-background flex-shrink-0">Original</TabsTrigger>
                  </TabsList>
                </div>
                
                <TabsContent value="diff" className="data-[state=inactive]:hidden flex-1 flex flex-col min-h-0 p-0 overflow-hidden">
                  <div className="flex-1 overflow-auto p-2 text-xs font-mono bg-background" style={{ minHeight: 0 }}>
                    {diff.map((part, index) => {
                      const lines = part.value.split('\n');
                      // Remove last empty line if it exists
                      if (lines[lines.length - 1] === '') {
                        lines.pop();
                      }
                      
                      return lines.map((line, lineIndex) => {
                        if (line === '' && lineIndex === lines.length - 1) return null;
                        
                        let bgColor = '';
                        let prefix = '  ';
                        if (part.added) {
                          bgColor = 'bg-green-500/20';
                          prefix = '+ ';
                        } else if (part.removed) {
                          bgColor = 'bg-red-500/20';
                          prefix = '- ';
                        }
                        
                        return (
                          <div
                            key={`${index}-${lineIndex}`}
                            className={`${bgColor} px-2 py-0.5 whitespace-pre ${part.added || part.removed ? 'font-semibold' : ''}`}
                          >
                            <span className="text-muted-foreground mr-2 select-none inline-block w-4">{prefix}</span>
                            <span className={part.removed ? 'text-red-400' : part.added ? 'text-green-400' : ''}>
                              {line || ' '}
                            </span>
                          </div>
                        );
                      });
                    })}
                  </div>
                </TabsContent>
                
                <TabsContent value="edited" className="data-[state=inactive]:hidden flex-1 flex flex-col min-h-0 p-0 overflow-hidden">
                  <div className="flex-1 overflow-auto p-2 text-xs font-mono bg-background" style={{ minHeight: 0 }}>
                    <code className="whitespace-pre">{editedScript}</code>
                  </div>
                </TabsContent>
              
                <TabsContent value="original" className="data-[state=inactive]:hidden flex-1 flex flex-col min-h-0 p-0 overflow-hidden">
                  <div className="flex-1 overflow-auto p-2 text-xs font-mono bg-muted/30" style={{ minHeight: 0 }}>
                    <code className="whitespace-pre">{currentScript}</code>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          );
        })()}
      </div>

      {/* Version History Modal */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>
              View and restore previous versions of the generated code
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 py-4">
            {artifactVersions.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No versions yet</p>
                <p className="text-xs mt-1">Versions will be created when you apply changes</p>
              </div>
            ) : (
              [...artifactVersions]
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .map((version) => (
                  <div
                    key={version.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      version.isCurrent
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    }`}
                    onClick={() => {
                      restoreArtifactVersion(version.id);
                      setShowVersionHistory(false);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={version.isCurrent ? 'default' : 'outline'}>
                          {version.alias}
                        </Badge>
                        {version.isCurrent && (
                          <Badge variant="secondary" className="text-[10px]">
                            Current
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {version.description || version.alias}
                        </span>
                        {version.isCurrent && version.alias.includes('.') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs ml-1"
                            title="Mark as major (start v2, v3...)"
                            onClick={(e) => {
                              e.stopPropagation();
                              promoteCurrentToMajor(version.id);
                            }}
                          >
                            <GitBranch className="w-3 h-3 mr-1" />
                            Major
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>{version.timestamp.toLocaleTimeString()}</span>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground font-mono line-clamp-2">
                      {version.code.substring(0, 100)}...
                    </div>
                  </div>
                ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVersionHistory(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Code Preview Modal */}
      <Dialog 
        open={showCodeModal} 
        onOpenChange={(open) => {
          setShowCodeModal(open);
          if (!open) {
            // Reset state when closing
            setCodeToUse('');
            setCodeValidationErrors([]);
            setIsValidatingCode(false);
            setShowDiff(false);
            if (validationTimeoutRef.current) {
              clearTimeout(validationTimeoutRef.current);
              validationTimeoutRef.current = null;
            }
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Review Generated Code</DialogTitle>
            <DialogDescription>
              Review the code before applying it to your script. You can edit it if needed.
            </DialogDescription>
          </DialogHeader>
          
          {/* Tabs for Code View and Diff View */}
          <Tabs value={showDiff ? 'diff' : 'code'} onValueChange={(value) => setShowDiff(value === 'diff')} className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between border-b">
              <TabsList>
                <TabsTrigger value="code">Code</TabsTrigger>
                <TabsTrigger value="diff">Diff</TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="code" className="data-[state=inactive]:hidden flex-1 flex flex-col min-h-0 mt-2" style={{ minHeight: '400px' }}>
              <div className="flex-1 border rounded overflow-hidden" style={{ minHeight: '400px', height: '60vh' }}>
                <MonacoEditor
                  value={codeToUse}
                  onChange={(newCode) => {
                    const code = newCode || '';
                    setCodeToUse(code);
                    
                    // Clear previous timeout
                    if (validationTimeoutRef.current) {
                      clearTimeout(validationTimeoutRef.current);
                    }
                    
                    // Validate syntax when user modifies code (debounced)
                    if (code.trim().length > 10) {
                      setIsValidatingCode(true);
                      // Debounce validation (500ms)
                      validationTimeoutRef.current = setTimeout(async () => {
                        try {
                          const response = await apiClient.post('/scripts/compile', { dslCode: code });
                          const compilation = response.data;
                          
                          if (compilation.success) {
                            setCodeValidationErrors([]);
                          } else {
                            setCodeValidationErrors(compilation.errors || []);
                          }
                        } catch (err) {
                          // Validation failed, but don't block user
                          console.error('Syntax validation error:', err);
                        } finally {
                          setIsValidatingCode(false);
                        }
                      }, 500);
                    } else {
                      setCodeValidationErrors([]);
                      setIsValidatingCode(false);
                    }
                  }}
                  language="quantquack-dsl"
                  height="60vh"
                  readOnly={false}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="diff" className="data-[state=inactive]:hidden flex-1 flex flex-col min-h-0 mt-2" style={{ minHeight: '400px' }}>
              <div className="flex-1 overflow-auto border rounded p-2 text-xs font-mono bg-muted/30" style={{ minHeight: '400px', height: '60vh' }}>
                {codeToUse && currentScript ? (
                  <div className="space-y-0">
                    {diffLines(currentScript, codeToUse).map((part, index) => {
                      const lines = part.value.split('\n');
                      // Remove last empty line if it exists
                      if (lines[lines.length - 1] === '') {
                        lines.pop();
                      }
                      
                      return lines.map((line, lineIndex) => {
                        if (line === '' && lineIndex === lines.length - 1) return null;
                        
                        let bgColor = '';
                        let prefix = '  ';
                        if (part.added) {
                          bgColor = 'bg-green-500/20';
                          prefix = '+ ';
                        } else if (part.removed) {
                          bgColor = 'bg-red-500/20';
                          prefix = '- ';
                        }
                        
                        return (
                          <div
                            key={`${index}-${lineIndex}`}
                            className={`${bgColor} px-2 py-0.5 whitespace-pre ${part.added || part.removed ? 'font-semibold' : ''}`}
                          >
                            <span className="text-muted-foreground mr-2 select-none inline-block w-4">{prefix}</span>
                            <span className={part.removed ? 'text-red-400' : part.added ? 'text-green-400' : ''}>
                              {line || ' '}
                            </span>
                          </div>
                        );
                      });
                    })}
                  </div>
                ) : (
                  <div className="text-muted-foreground text-center py-8">
                    No code to compare
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="mt-2">
            {isValidatingCode && (
              <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Validating syntax...
              </div>
            )}
            {codeValidationErrors.length > 0 && (
              <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs">
                <p className="font-semibold text-destructive mb-1">⚠️ Syntax Warnings Found:</p>
                <ul className="list-disc list-inside space-y-1">
                  {codeValidationErrors.map((error, index) => (
                    <li key={index} className="text-destructive">
                      {error.line ? `Line ${error.line}: ` : ''}{error.message}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-muted-foreground">
                  You can still use this code, but it may not compile. Consider fixing these issues before applying.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                // Clear validation timeout
                if (validationTimeoutRef.current) {
                  clearTimeout(validationTimeoutRef.current);
                  validationTimeoutRef.current = null;
                }
                setShowCodeModal(false);
                setCodeToUse('');
                setCodeValidationErrors([]);
                setIsValidatingCode(false);
                setShowDiff(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const cleanedCode = codeToUse.trim();
                if (cleanedCode) {
                  // Clear validation timeout
                  if (validationTimeoutRef.current) {
                    clearTimeout(validationTimeoutRef.current);
                    validationTimeoutRef.current = null;
                  }
                  
                  setEditedScript(cleanedCode);
                  setShowCodeModal(false);
                  setCodeToUse('');
                  setCodeValidationErrors([]);
                  setIsValidatingCode(false);
                  setShowDiff(false);
                  
                  if (codeValidationErrors.length > 0) {
                    showSuccessToast('Code extracted (with syntax warnings). Check Edit tab to review and apply.');
                  } else {
                    showSuccessToast('Code extracted. Check Edit tab to apply.');
                  }
                }
              }}
              disabled={isValidatingCode || !codeToUse.trim()}
              variant={codeValidationErrors.length > 0 ? 'default' : 'default'}
            >
              <Check className="w-4 h-4 mr-2" />
              {codeValidationErrors.length > 0 ? 'Use Code Anyway' : 'Use This Code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default AIScriptEditor;
